import angular from 'angular';
import ngAnimate from 'angular-animate';
/** Styles */
import './WriteReceipts.scss';
/** Menu */
import booksMenu from '../../menu/Menu';
/** Templates */
import writeReceiptsTemplate from './WriteReceipts.html';
/** Services */
import { runDataGridPaymentItem } from './services/DGPaymentItem';
import { runDataGridReceiptItem } from './services/DGReceiptItem';
import { ReceiptsServiceProvider } from './services/ReceiptsService';
/** Dom */
import { receiptProductInputComponent } from './dom/receiptProductInput';
/** Dialogs */
import voidEntryDialog from '../../com/VoidEntryDialog/VoidEntryDialog';
import limitedReceiptItemsDialog from '../../com/LimitedReceiptItemsDialog/LimitedReceiptItemsDialog';
import receiptsEntityInfoDialog from '../../com/ReceiptsEntityInfoDialog/ReceiptsEntityInfoDialog';
import creditMemoDialog from '../../com/CreditMemoDialog/CreditMemoDialog';
import paymentDistributionDialog from '../../com/PaymentDistributionDialog/PaymentDistributionDialog';
import { FindReceiptDialogService } from './dialogs/FindReceiptDialog';

export default angular
  .module('csm.books.WriteReceipts', [
    booksMenu,
    voidEntryDialog,
    ngAnimate,
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.cellNav',
    limitedReceiptItemsDialog,
    receiptsEntityInfoDialog,
    creditMemoDialog,
    paymentDistributionDialog
  ])

  /*******************************************************************************
   * Component: writeReceipts
   *
   * This directive is used for standard receipt entry and review. This directive
   * uses transclusion slots to populate the DOM with the appropriate product and
   * other amount input sections.
   *
   * scope:
   *
   * @attr {object} config Provides overrides to the default configurations
   *       utilized in the ReceiptsController
   *
   * transclusion slot: 'otherInput' DOM content for other amount input
   ******************************************************************************/
  .component('writeReceipts', {
    bindings: { config: '=' },
    transclude: { otherInput: 'otherInput' },
    template: writeReceiptsTemplate,
    controller: ReceiptsController
  })

  /*******************************************************************************
  * ReceiptEventTypes
  /** 
  * Constants to be used with event dispatching
  * 
  * @ngdoc constant
  * @memberof csm.books.WriteReceipts
  * @name csm.books.WriteReceipts.constant:ReceiptEventTypes
  ******************************************************************************/
  .constant('ReceiptEventTypes', {
    EVENT_EDIT_ACCOUNT: 'editAccount',
    EVENT_EDIT_PRODUCT: 'editProduct',
    EVENT_FOCUS_ACCOUNT: 'focusAccount',
    EVENT_FOCUS_PRODUCT: 'focusProduct',
    EVENT_RESET_ACCOUNT: 'resetAccount',
    EVENT_RESET_PRODUCT: 'resetProduct'
  })
  .factory('Data', initDataFactory)
  .run(runDataGridPaymentItem)
  .run(runDataGridReceiptItem)
  .provider('ReceiptsService', ReceiptsServiceProvider)
  .component('receiptProductInput', receiptProductInputComponent)
  .service('FindReceiptDialog', FindReceiptDialogService).name;

initDataFactory.$inject = ['SERVER'];

/*******************************************************************************
 * initDataFactory */
/**
 * Returns the initial (empty) data object.
 *******************************************************************************/
/* @ngInject */ function initDataFactory(SERVER) {
  return {};
}

/*******************************************************************************
 * ReceiptsController */
/**
 * Controller for the Write Receipts app.
 *******************************************************************************/
function ReceiptsController(
  $scope,
  $element,
  $filter,
  $q,
  $timeout,
  $uibModal,
  Alert,
  BarcodeValidator,
  SERVER,
  BooksSettings,
  BooksSvc,
  BusyScreen,
  CommonUIGridTemplates,
  CreditMemoDialog,
  Data,
  EntityMaker,
  FindReceiptDialog,
  LimitedReceiptItemsDialog,
  PaymentDistributionDialog,
  ReceiptsEntityInfoDialog,
  ReceiptEventTypes,
  ReceiptsService,
  Tools,
  uiGridConstants,
  VoidEntryDialog
) {
  var $ctrl = this;

  /*****************************************************************************
   * Constants
   ****************************************************************************/
  /** Payment Button IDs. */
  var PMT_BUTTON_ID_CASH = 'btnCash';
  var PMT_BUTTON_ID_CHECK = 'btnCheck';
  var PMT_BUTTON_ID_CARD = 'btnCard';
  var PMT_BUTTON_ID_FUNDS = 'btnFunds';

  /** Status Configurations */ var mStatusConfig = {
    cleared: { status: 'CLEARED', bgClass: 'bg-success' },
    voided: { status: 'VOIDED', bgClass: 'bg-danger' },
    deposited: { status: 'DEPOSITED', bgClass: 'bg-info' },
    returned: { status: 'RETURNED', bgClass: 'bg-danger' },
    partiallyReturned: { status: 'PARTIALLY RETURNED', bgClass: 'bg-warning' }
  };

  /*****************************************************************************
   * Properties
   ****************************************************************************/
  /** Default Configurations. */
  var DEFAULT_CONFIG = {
    /** Label for the account panel */ acctPanelLabel: 'Accounts',
    /** Allow purchasing on account. */ allowOnAccount: true,
    /** Allow spending from student funds. */ allowStudentFunds: true,
    /** Default Handwritten Value */ defaultHandwritten: false,
    /** Default date */ defaultDate: new Date(),
    /** Load classes. */ includeClasses: true,
    /** Include notes panel */ includeNotes: true,
    /** Open/collapse the accounts panel */ openAccountTab: false,
    /** Hides/shows unpaid items in modal */ showUnpaidItems: true
  };

  /** Receipt Watcher Deregistration function. */
  var mReceiptWatcher = null;

  /**
   * Saved Student Funds Detail. Used with validation student fund amounts for
   * existing receipts.
   */
  var mSavedFunds = null;

  /** Manual Credit Card Processor. */ var mManualProcessor = null;
  /** Undeposited Funds Account ID for Cash/Checks. */ var mUFAccountID = NaN;
  /** User's role can change a saved item's effective date (Bookkeeper or
    District Bookkeeper, President or Treasurer in PTA/Booster). The server
    enforces this on save. */ var mCanEditItemEffectiveDate = false;
  /** Product List. */ var mProductList = null;

  /*---------------*/
  /* SWF/App Flags */
  /*---------------*/
  /** Disable Void. */ var mDisableVoid = false;
  /** Restrict Entities. */ var mRestrictAllEntities = false;
  /** Handwritten receipts. */ var mRestrictReptHand = false;

  /** UI Flags. */ var mUI_Flags = {};
  /** Allow card payments. */ Object.defineProperty(
    mUI_Flags,
    'allowCreditCards',
    {
      get: function () {
        return mManualProcessor && mManualProcessor.merchantAcctID;
      }
    }
  );
  /** Receipt Date/From are edittable. */ Object.defineProperty(
    mUI_Flags,
    'enableDateFrom',
    {
      get: function () {
        return (
          mUI_Flags.isNewReceipt ||
          !(
            mUI_Flags.isVoidDeposit ||
            mUI_Flags.isWebStore ||
            mUI_Flags.hasClearedCard
          )
        );
      }
    }
  );
  /** Enable handwritten flag. */ Object.defineProperty(
    mUI_Flags,
    'enableHandwritten',
    {
      get: function () {
        return (
          !mUI_Flags.isVoidDeposit &&
          mUI_Flags.isNewHandwritten &&
          !mUI_Flags.isReturnReceipt &&
          !mRestrictReptHand
        );
      }
    }
  );
  /** Enable items for add/edit/remove flag. */ Object.defineProperty(
    mUI_Flags,
    'enableItems',
    {
      get: function () {
        return (
          mUI_Flags.isNewReceipt ||
          !(
            (mUI_Flags.unvoidable && !mUI_Flags.isDeposited) ||
            mUI_Flags.isWebStore ||
            hasReceivablePayments() ||
            mUI_Flags.isVoided ||
            mUI_Flags.isReturnReceipt ||
            mUI_Flags.hasReturns
          )
        );
      }
    }
  );
  /** Enable the receipt number for change. */ Object.defineProperty(
    mUI_Flags,
    'enableNumber',
    {
      get: function () {
        return mUI_Flags.isHandwritten && mUI_Flags.enableHandwritten;
      }
    }
  );
  /** Is Handwritten Indication. */ Object.defineProperty(
    mUI_Flags,
    'isHandwritten',
    {
      get: function () {
        return $ctrl.receipt && $ctrl.receipt.handwritten;
      }
    }
  );
  /** New or handwritten receipt. */ Object.defineProperty(
    mUI_Flags,
    'hasKpsPayment',
    {
      get: function () {
        return Tools.isEmpty($ctrl.receipt)
          ? false
          : $ctrl.receipt.paymentItems.some(
              (payment) =>
                payment.processorType == SERVER.BooksDBConstants.PT_KPS
            );
      }
    }
  );
  /** New or handwritten receipt. */ Object.defineProperty(
    mUI_Flags,
    'isNewHandwritten',
    {
      get: function () {
        return mUI_Flags.isNewReceipt || mUI_Flags.isSavedAsHand;
      }
    }
  );
  /** New receipt. */ Object.defineProperty(mUI_Flags, 'isNewReceipt', {
    get: function () {
      return ($ctrl.receipt || { isNew: true }).isNew;
    }
  });
  /** Return receipt indicator. */ Object.defineProperty(
    mUI_Flags,
    'isReturnReceipt',
    {
      get: function () {
        return (
          ($ctrl.receipt || {}).type ==
          SERVER.BooksDBConstants.LET_RETURN_RECEIPT
        );
      }
    }
  );
  /** Void or Deposited flag. */ Object.defineProperty(
    mUI_Flags,
    'isVoidDeposit',
    {
      get: function () {
        return mUI_Flags.isVoided || mUI_Flags.isDeposited;
      }
    }
  );
  /** No Journal Items. */ Object.defineProperty(mUI_Flags, 'noJournalItems', {
    get: function () {
      return !$ctrl.receipt.hasJournalItems;
    }
  });

  /*-------------------------------------------------*/
  /* Template for the product Description column. If */
  /* the product is a subitem, it will be indented. */
  /*-------------------------------------------------*/
  var prodCellTemplate =
    '\
  <div style="display:block; height: 100%; z-index:1;" tabindex="0">\
    <div class="productMemoCell">\
      <span class="ui-grid-cell-contents" ng-class="{\'sub-item\':row.entity.isChildItem(), \'hasOptions\': row.entity.hasOptions()}"\
            uib-tooltip-html="row.entity.tooltipHtml" tooltip-class="receiptOptionsTooltip" tooltip-append-to-body="true">\
        {{grid.getCellValue(row, col)}}\
      </span>\
    </div>\
  </div>';

  /** App Configurations. */ var mAppConfig = angular.extend(
    {},
    DEFAULT_CONFIG
  );
  /** Loaded Receipt Payment Total. */ var mOriginalPaidTotal = 0;
  /** ReceivableCharge Amount (Old Receipts) */ var mReceivableCharge = 0;

  /**
   * PayPal Amount. This is the actual amount for the PayPal ledger item. In the
   * payments display, the receipt total is used, so this will need to be
   * maintained to preserve the integrity of the data.
   *
   * @private
   */
  var mPayPalAmount = 0;

  /*------------*/
  /* Selections */
  /*------------*/
  /** Current Receipt Index. */ var mCurrentIdx = -1;
  /** Current Receipt ID. */ var mReceiptID = NaN;
  /** Current entity object. */ var mSavedEntity = null;

  /*-------*/
  /* Flags */
  /*-------*/
  var mEntityMakerActive = false;
  let entityInfoDialogActive = false;

  /*****************************************************************************
   * Getters
   ****************************************************************************/
  /** Remaining amount to pay. */
  function amtRemaining() {
    return Tools.roundToPrecision($ctrl.salesTotal() - $ctrl.paymentTotal(), 2);
  }

  /**
   * Returns the fund amount that was applied on the original saved receipt
   * being editted when the Received From is the same as the entity for the
   * mSavedFunds.
   */
  function editFundAmount() {
    return mSavedFunds &&
      $ctrl.data.from &&
      mSavedFunds.entityID == $ctrl.data.from.entity.id
      ? mSavedFunds.amount
      : 0;
  }

  /**
   * Returns an indication if there is a membership item on the receipt to be
   * purchased.
   */
  function hasMembership() {
    return $ctrl.rcptGrid.data.some(function (dgItem) {
      return dgItem.isMembership();
    });
  }

  /**
   * Returns a list of receipt items to get any items that cannot be partially
   * paid. These will include payment items, donations, and any sundry items
   */
  function nonPaymentItems() {
    return $ctrl.rcptGrid.data.filter(function (dgItem) {
      return (
        !mAppConfig.allowOnAccount ||
        (!dgItem.isGroup() &&
          (dgItem.isAmountOnly() ||
            dgItem.isPayment() ||
            !Tools.findArrayItem(mProductList, 'id', dgItem.itemID(), {
              allowPayments: false
            }).allowPayments))
      );
    });
  }

  /** Returns a list of sale items that can have payments made against them. */
  function paymentSaleItems() {
    var nonPaymentList = nonPaymentItems();

    return $ctrl.rcptGrid.data.filter(function (dgItem) {
      return !dgItem.isGroup() && nonPaymentList.indexOf(dgItem) == -1;
    });
  }

  /*****************************************************************************
   * Setter/Getters
   ****************************************************************************/
  $ctrl.handwritten = function (value) {
    if (!$ctrl.receipt) return;

    /*---------------------------------------------------------*/
    /* Sets the properties for the checkbox on page load based */
    /* on the value of the mAppConfig.defaultHandwritten flag */
    /*---------------------------------------------------------*/
    if (!arguments.length)
      return mAppConfig.defaultHandwritten
        ? !$ctrl.receipt.handwritten
        : $ctrl.receipt.handwritten;

    /*--------------------------------------------------------------*/
    /* Maintains the handwritten value and receiptNumber when the */
    /* box is checked. When the mAppConfig.defaultHandwritten */
    /* value is true, we want to take the opposite of the value */
    /*--------------------------------------------------------------*/
    $ctrl.receipt.handwritten = mAppConfig.defaultHandwritten ? !value : value;
    $ctrl.receipt.receiptNumber = $ctrl.receipt.handwritten
      ? $ctrl.data.dbNextRcptNumHandwritten
      : $ctrl.data.dbNextRcptNumPrinted;
  };

  /*****************************************************************************
   * Property : hasReceivablePayments
   */
  /*****************************************************************************
   * Indication that this receipt has payments for at least one receivable item.
   *
   * @private
   ****************************************************************************/
  function hasReceivablePayments() {
    return ($ctrl.rcptGrid.data || []).some(function (dgi) {
      return dgi.isPayment();
    });
  }

  /*****************************************************************************
   * receiptNumber
   */
  /*****************************************************************************
   * Formats the input for the receipt number. When the receipt is handwritten
   * and the input does not have focus, an 'h' is added to the end of the
   * number. This is done for display purposes only, the 'h' is removed before
   * the value is submitted.
   *
   * @param value
   *          Value in the input field
   ****************************************************************************/
  $ctrl.receiptNumber = function (value) {
    if (!$ctrl.receipt) return;

    if (!arguments.length) {
      /*------------------------------------------*/
      /* Add 'h' when the receipt is handwritten */
      /* and the focus is not on the number field */
      /*------------------------------------------*/
      var hasFocus =
        document.activeElement ==
        document.querySelector('input[ng-model=receiptNumber]');

      /*---------------------------------------------------------*/
      /* When the field doesn't have focus use the display value */
      /* which will be in the format {batch-}receiptNumber{h} */
      /*---------------------------------------------------------*/
      var displayNumber =
        ($ctrl.receipt.batchNumber ? $ctrl.receipt.batchNumber + '-' : '') +
        $ctrl.receipt.receiptNumber +
        ($ctrl.receipt.handwritten ? 'h' : '');

      return hasFocus ? $ctrl.receipt.receiptNumber : displayNumber;
    }

    if (!$ctrl.receipt.handwritten) return;

    /*-------------------------------------------------------------*/
    /* When setting the receiptNumber remove the h if it is there. */
    /*-------------------------------------------------------------*/
    $ctrl.receipt.receiptNumber = Number(value.toString().replace(/\D/, ''));
  };

  /*****************************************************************************
   * totalTax
   */
  /*****************************************************************************
   * Returns the tax amount for all items.
   ****************************************************************************/
  function totalTax() {
    var total = 0;

    $ctrl.rcptGrid.data.forEach(function (item) {
      total += item.tax();
    });

    return total;
  }

  /*****************************************************************************
   * addCash
   */
  /*****************************************************************************
   * Called when the add cash button is clicked. Adds a row to the <i>payGrid</i>
   * with a type of LIT_MONEY_RECEIVED and subType of LIS_CASH_RECEIVED if a row
   * does not already exist.
   ****************************************************************************/
  function addCash() {
    var cashItem =
      $ctrl.payGrid.data
        .filter(function (dgItem) {
          return dgItem.isCash();
        })
        .shift() ||
      new Data.DGPaymentItem(
        SERVER.BooksDBConstants.LIT_MONEY_RECEIVED,
        SERVER.BooksDBConstants.LIS_CASH_RECEIVED,
        amtRemaining()
      );

    addPaymentDGItem(cashItem);
  }

  /*****************************************************************************
   * addCard
   */
  /*****************************************************************************
   * Called when the add card button is clicked. Adds a row to the <i>payGrid</i>
   * for a manual credit card recording.
   ****************************************************************************/
  function addCard() {
    if (!mUI_Flags.allowCreditCards) return;

    addPaymentDGItem(
      new Data.DGPaymentItem(
        SERVER.BooksDBConstants.LIT_MONEY_RECEIVED,
        SERVER.BooksDBConstants.LIS_CREDIT_CARD,
        amtRemaining(),
        SERVER.BooksDBConstants.PT_MANUAL
      )
    );
  }

  /*****************************************************************************
   * addCheck
   */
  /*****************************************************************************
   * Called when the add check button is clicked. Adds a row to the <i>payGrid</i>
   * with a type of LIT_MONEY_RECEIVED and subType of LIS_CHECK_RECEIVED.
   ****************************************************************************/
  function addCheck() {
    addPaymentDGItem(
      new Data.DGPaymentItem(
        SERVER.BooksDBConstants.LIT_MONEY_RECEIVED,
        SERVER.BooksDBConstants.LIS_CHECK_RECEIVED,
        amtRemaining()
      )
    );
  }

  /*****************************************************************************
   * addFunds
   */
  /*****************************************************************************
   * Called when the add funds button is clicked. Adds a row to the <i>payGrid</i>
   * with a type of LIT_STUDENT_FUNDS_DEBIT if a row doesn't already exist.
   ****************************************************************************/
  function addFunds() {
    if (!$ctrl.studentFunds()) return;

    var fundItem =
      $ctrl.payGrid.data
        .filter(function (dgItem) {
          return dgItem.isFundDebit();
        })
        .shift() ||
      new Data.DGPaymentItem(
        SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_DEBIT,
        0,
        Math.min(amtRemaining(), $ctrl.studentFunds())
      );

    fundItem.source().entityID = $ctrl.data.from.entity.id;

    addPaymentDGItem(fundItem);
  }

  /*****************************************************************************
   * addPaymentDGItem
   */
  /*****************************************************************************
   * Adds a DGPaymentItem to the payment datagrid if the passed item is not
   * already in the grid. Focus is put on the first editable column for the
   * item.
   *
   * @param dgPayment
   *          DGPaymentItem to add, set focus on
   ****************************************************************************/
  function addPaymentDGItem(dgPayment) {
    var isNew = $ctrl.payGrid.data.indexOf(dgPayment) == -1;

    /*--------------------------------------------------------------------------*/
    /* For new payments, ensure previous payments of the same type and subtype */
    /* have all necessary data filled in. If not, update the dgPayment item */
    /* reference to be the most recent incomplete item so the add is prevented. */
    /*--------------------------------------------------------------------------*/
    if (isNew)
      dgPayment =
        $ctrl.payGrid.data
          .filter(function (dgp) {
            return (
              dgp.source().type == dgPayment.source().type &&
              dgp.source().subType == dgPayment.source().subType &&
              (!dgp.allowDetailEdit() ||
                !dgp.number ||
                !dgp.name ||
                !dgp.amount)
            );
          })
          .shift() || dgPayment;

    /*---------------------------------------------------------*/
    /* Add the payment if it doesn't exist in the grid already */
    /*---------------------------------------------------------*/
    if ($ctrl.payGrid.data.indexOf(dgPayment) == -1) {
      $ctrl.payGrid.data.push(dgPayment);

      /*-------------------------------------------*/
      /* Ensure data has been rendered by the Grid */
      /*-------------------------------------------*/
      $ctrl.payGrid.api.grid.modifyRows($ctrl.payGrid.data);
      setModified(true);
    }

    /*-------------------------------------------------------*/
    /* Set focus on the appropriate field for value updating */
    /* by finding the first editable column for the payment */
    /* that has no data, otherwise use the amount column */
    /*-------------------------------------------------------*/
    var colDef = $ctrl.payGrid.columnDefs
      .filter(function (col) {
        /*--------------------------------------------------------------*/
        /* Utilize the cellEditableCondition function, it expects a row */
        /* scope, so a dummy object is passed with the same structure. */
        /*--------------------------------------------------------------*/
        return (
          col.enableCellEdit &&
          col.cellEditableCondition({ row: { entity: dgPayment } }) &&
          (!dgPayment[col.field] || col.field == 'amount')
        );
      })
      .shift();

    if (colDef) {
      /*------------------------------------------------------------*/
      /* Timeout is used here so that the scrollToFocus is not      */
      /* called before the scroll bar is rendered. This solves the  */
      /* problem of the fifth row not defaulting to being editable. */
      /*------------------------------------------------------------*/
      $timeout(function () {
        /*--------------------------------------------------------*/
        /* Set focus to first column, this is needed as editting  */
        /* will only start if the row or column has changed focus */
        /*--------------------------------------------------------*/
        $ctrl.payGrid.api.cellNav.scrollToFocus(
          dgPayment,
          $ctrl.payGrid.columnDefs[0]
        );

        /*----------------------------------------------*/
        /* Set focus on appropriate column for editting */
        /*----------------------------------------------*/
        $ctrl.payGrid.api.cellNav.scrollToFocus(dgPayment, colDef);
      });
    }
  }

  /*****************************************************************************
   * applyDiscount
   */
  /*****************************************************************************
   * Goes through each item and attempts to apply a discount.
   ****************************************************************************/
  function applyDiscount() {
    if ($ctrl.rcptGrid.data)
      $ctrl.rcptGrid.data.forEach(function (item) {
        if (item.isChildItem()) return;

        /*--------------------------------------------------*/
        /* If the item already has an entity, we will use   */
        /* that, otherwise use the entity from the receipt. */
        /*--------------------------------------------------*/
        item.applyDiscount(
          item.source().entity || ($ctrl.data.from || {}).entity,
          hasMembership()
        );
      });
  }

  /*****************************************************************************
   * clearReceipt
   */
  /*****************************************************************************
   * Verifies the current receipt can be cleared from the UI before calling the
   * <b>method</b>. If the current receipt info has been modified, the user
   * must confirm canceling the changes before the <b>method</b> is called. If
   * there are no changes, <b>method</b> is called directly. If the user
   * declines to confirm clearing a modified receipt, focus is set on
   * <b>domElement</b>.
   *
   * @param domElement
   *          Component to focus if user declines.
   * @param method
   *          Method to call if receipt can be cleared.
   * @private
   ****************************************************************************/
  var clearReceipt = function (domElement, method) {
    if ($ctrl.receipt != null && $ctrl.receipt.modified) {
      var title = mUI_Flags.isNewReceipt
        ? 'Cancel New Receipt'
        : 'Cancel Changes';
      var msg = mUI_Flags.isNewReceipt
        ? 'Are you sure you want to cancel this Receipt?'
        : 'You have made changes to this receipt.\nAre you sure you want to cancel these changes?';
      Alert.confirm(msg, title).then(method, function () {
        domElement.focus();
      });
    } else method();
  };

  /*****************************************************************************
   * deleteItem
   */
  /*****************************************************************************
   * Called when the provided dgReceiptItem is requesting to be removed.
   *
   * @param dgReceiptItem
   *          DGReceiptItem to remove
   ****************************************************************************/
  function deleteItem(dgReceiptItem) {
    if (!$ctrl.allowItemUpdates()) {
      Alert.info("Receipt doesn't allow item updates.", "Can't remove item");
      return;
    }

    removeItem(dgReceiptItem);
    setModified(true);
  }

  /*****************************************************************************
   * deletePayment
   */
  /*****************************************************************************
   * Called when the provided dgPayment is requesting to be removed. This checks
   * that the payment can be deleted before actually performing the delete.
   *
   * @param dgPayment
   *          DGPaymentItem to remove
   ****************************************************************************/
  function deletePayment(dgPayment) {
    if (dgPayment.hideDelete()) return;

    if (!$ctrl.allowItemUpdates() || mUI_Flags.isDeposited) {
      Alert.info(
        "This receipt doesn't allow payment changes.",
        "Can't remove payment"
      );
      return;
    }

    if (dgPayment.isNSF()) {
      Alert.info('Check is NSF.', "Can't remove payment");
      return;
    }

    /*---------------------*/
    /* Remove the payment. */
    /*---------------------*/
    var index = $ctrl.payGrid.data.indexOf(dgPayment);
    $ctrl.payGrid.data.splice(index, 1);
    setModified(true);
  }

  /*****************************************************************************
   * entityInfo
   */
  /*****************************************************************************
   * Handles the EntityInfo panel to show the entity information and any unpaid
   * items/fines/receipts, as well as any currently open orders. The dialogs
   * promise will resolve with a list of receivable items to pay.
   *
   * @param entityOrBarcode
   *          passed in entity or barcode
   * @param alwaysShow
   *          Forces the dialog to open even if there is no data to display.
   * @param allowItemUpdates()
   *          Receipts that can be updated.
   * @param showUnpaidItems
   *          Boolean, shows/hides the unpaid items in the modal
   ****************************************************************************/
  var entityInfo = function (entityOrBarcode, alwayShow) {
    /*-------------------------------------------------*/
    /* Clear the receipt number so that they don't try */
    /* to create a new receipt with this as the From. */
    /*-------------------------------------------------*/
    if (!angular.isObject($ctrl.data.from)) $ctrl.data.from = null;

    /*---------------------------------------*/
    /* Notifier object to pass to the dialog */
    /*---------------------------------------*/
    var notifiers = {
      /**
       * If there is a payment for the credited receivable as a new item then it
       * will be removed.
       */
      creditMemo: function (receivable) {
        var receiptData = $ctrl.rcptGrid.data;
        var refID = receivable.referenceID || receivable.referenceItemID;

        for (var i = 0; i < receiptData.length; i++) {
          var rgData = receiptData[i];
          var gridRefID =
            rgData.source().referenceID || rgData.source().referenceItemID;

          /*-----------------------------------------------------------------*/
          /* If the receipt in the grid is new, is a payment and matches the */
          /* receipt receiving credit it is removed and the loop is stopped. */
          /*-----------------------------------------------------------------*/
          if (rgData.isNew() && rgData.isPayment() && gridRefID == refID) {
            deleteItem(rgData);
            break;
          }
        }
      },

      /**
       * Update the next available receipt numbers for the pickup receipt
       * generation. If this is a new receipt, update the receipt number.
       */
      pickup: function () {
        loadNextNumbers().then(function () {
          if (mUI_Flags.isNewReceipt)
            $ctrl.receipt.receiptNumber = $ctrl.receipt.handwritten
              ? $ctrl.data.dbNextRcptNumHandwritten
              : $ctrl.data.dbNextRcptNumPrinted;
        });
      }
    };

    /***
     * This prevents a visual glitch where the dialog is opened twice. Before when a user type in an entity and they hit the "Info" button
     * as their first action, the dialog would open twice. This is because the dialog is opened by the on blur event and the client event of the button.
     *
     * This checks to see if the dialog exists in the DOM and if it does, we short circuit the function.
     **/
    if (entityInfoDialogActive) return;

    entityInfoDialogActive = true;

    const barcode = angular.isString(entityOrBarcode) ? entityOrBarcode : null;
    const entity = angular.isObject(entityOrBarcode) ? entityOrBarcode : null;

    /*------------------------------------------*/
    /* The dialog will resolve with an array of */
    /* receivableItems to make payments against */
    /*------------------------------------------*/
    ReceiptsEntityInfoDialog.show(
      entity || $ctrl.data.from,
      barcode,
      alwayShow,
      $ctrl.allowItemUpdates(),
      mAppConfig.showUnpaidItems,
      notifiers
    )
      .then((data) => {
        data.forEach((ri) => {
          /*------------------------------------------------------------*/
          /* If there is already a payment to this receivable item,     */
          /* simply reset the payment amount to the receivable balance. */
          /*------------------------------------------------------------*/
          const current = $ctrl.rcptGrid.data
            .filter((dgItem) => {
              /*--------------------------------------------------------------*/
              /* The source for the dgItem refers to the original receivable  */
              /* credit. The receivable has its own source which refers to    */
              /* what it was created from (item, fine, Destiny). For anything */
              /* created from our system, the dgSource will be enough. For    */
              /* Destiny fines, we need to look at the source of dgSource.    */
              /*--------------------------------------------------------------*/
              const dgSource = dgItem.source();
              const hasReferenceItemID = Boolean(dgSource.referenceItemID);
              const hasSourceFineID = Boolean(
                dgSource.source && dgSource.source.fineID
              );

              return (
                (hasReferenceItemID &&
                  dgSource.referenceItemID == ri.referenceItemID) ||
                (hasSourceFineID &&
                  ri.source &&
                  dgSource.source.fineID == ri.source.fineID)
              );
            })
            .shift();

          if (current) {
            current.amount = ri.amountToPay;
            return;
          }

          /*------------------------------------------------------*/
          /* Else, add a payment item for the current receivable. */
          /*------------------------------------------------------*/
          const receiptItem = {
            source: ri,
            type: SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT,
            entityID: ri.entityID,
            name: ri.entityName,
            accountID: ri.accountID,
            /**
             * The amount to pay is set by the user the users are allowed to pay less than the full amount.
             */
            amount: ri.amountToPay,
            /**
             * We keep the original remaining balance on the receivable item to ensure that the user can't pay more than the
             * original amount.
             */
            balance: ri.balance
          };

          /*--------------*/
          /* Item Payment */
          /*--------------*/
          if (ri.forItem) {
            angular.extend(receiptItem, {
              subType: SERVER.BooksDBConstants.LIS_ITEM_PAYMENT,
              referenceItemID: ri.referenceItemID,
              memo: 'Payment to {0} on Receipt #{1}'.format(
                ri.description,
                ri.receiptNumber
              )
            });
          } else if (!ri.itemID) {
            /*----------------------------------------------*/
            /* Destiny Fine Payment                         */
            /*                                              */
            /* TODO-CK This is a hack, and we should try to */
            /* find a better way of differentiating them.   */
            /*----------------------------------------------*/
            angular.extend(receiptItem, {
              subType: SERVER.BooksDBConstants.LIS_DESTINY_FINE_PAYMENT,
              memo: 'Destiny Fine Payment: ' + ri.description,
              source: ri.source
            });
          } else {
            /*--------------*/
            /* Fine Payment */
            /*--------------*/
            angular.extend(receiptItem, {
              subType: SERVER.BooksDBConstants.LIS_FINE_PAYMENT,
              referenceItemID: ri.referenceItemID,
              itemID: ri.itemID,
              memo: 'Fine Payment: ' + ri.description
            });
          }

          $ctrl.addReceiptItem(receiptItem);
        });
      })
      .finally(() => {
        /***
         * We use .finally here to ensure the dialog open flag is reset properly if the dialog promise is rejected or resolved. This prevents
         * duplicate code by having to reset the flag in both the resolve and reject functions.
         * **/
        entityInfoDialogActive = false;
      });
  };

  /*****************************************************************************
   * findReceipt */
  /**
   * Handler for `Find`, `Next`, and `Previous` buttons. `Find` shows the
   * `FindReceiptDialog`. `Next`/`Previous` load the next receipt found in the
   * list of receipts from the last `Find` or from the current receipt ID in the
   * database.
   *
   * @param {int} dir 0/null/undefined=Find, >0=Next, <0=Previous.
   *****************************************************************************/
  function findReceipt(dir) {
    var endOfList = function () {
      Alert.info('There are no more receipts', 'No More Receipts');
    };

    /*-------------------------------------------------------------*/
    /* Find button. Show FindReceiptDialog and load selected       */
    /* receipt to the UI.                                          */
    /*                                                             */
    /* Next/Previous buttons will scan the list of receipts found. */
    /*-------------------------------------------------------------*/
    if (!dir)
      FindReceiptDialog.show().then(function (receipt) {
        mCurrentIdx = FindReceiptDialog.foundReceipts.indexOf(receipt);

        clearReceipt(document.getElementById('btnFind'), function () {
          getReceipt(receipt.id);
        });

        /*-----------------------------------------------------*/
        /* If only one receipt found, clear the dialog results */
        /* so next/previous will scan database from here.      */
        /*-----------------------------------------------------*/
        if (FindReceiptDialog.foundReceipts.length == 1)
          FindReceiptDialog.reset();
      });
    /*----------------------------------------------------------------*/
    /* Next/Previous, scanning list of receipts found with            */
    /* FindReceiptDialog. Load the Next/Previous receipt in the list. */
    /*----------------------------------------------------------------*/ else if (
      FindReceiptDialog.foundReceipts.length
    ) {
      mCurrentIdx += dir > 0 ? 1 : -1;

      if (
        mCurrentIdx >= 0 &&
        mCurrentIdx < FindReceiptDialog.foundReceipts.length
      )
        getReceipt(FindReceiptDialog.foundReceipts[mCurrentIdx].id);
      else endOfList();
    } else if (dir > 0 && !mReceiptID) endOfList();
    /*-------------------------------------*/
    /* Next button, scanning the database, */
    /* requires a currently loaded receipt.  */
    /*-------------------------------------*/
    /*-------------------------------------------*/
    /* Next/Previous, scanning the database.     */
    /* Load the receipt with the Next/Previous ID. */
    /*-------------------------------------------*/ else
      getReceipt(mReceiptID, dir).catch(function () {
        if (dir > 0) startNewReceipt();
        else endOfList();
      });
  }

  /*****************************************************************************
   * generateItemCharge
   */
  /*****************************************************************************
   * Generates a ReceivableItemCharge object for the given referenceItemID and
   * amount.
   *
   * @param referenceItemID
   *          ReceiptItem/LedgerItem ID to reference
   * @param amount
   *          Amout to put on account/charge
   * @param entityID
   *          Who this specific charge is FOR - the referenced item sale's own
   *          entityID, not the receipt's. Falls back to $ctrl.receipt.entityID
   *          only when the item itself has no distinct entity (e.g. a
   *          non-family-member sale) - previously this always used the
   *          receipt's entity unconditionally, which recorded the purchaser
   *          as the debtor instead of who the item was actually sold to
   *          whenever those two differ (e.g. a parent buying for their kid).
   * @param entityName
   *          Display name paired with entityID above - the referenced item
   *          sale's own name (set on the ReceiptItem in receiptProductInput.js
   *          whenever it's sold to a distinct entity), not the receipt's.
   *          Falls back to $ctrl.receipt.receiptFrom for the same reason/only
   *          when entityID has no distinct entity - previously this was
   *          hardcoded to the receipt's name unconditionally, so the charge
   *          displayed the purchaser's name instead of who the item was
   *          actually sold to whenever those two differ.
   * @return ReceivableItemCharge object
   ****************************************************************************/
  function generateItemCharge(referenceItemID, amount, entityID, entityName) {
    return (
      0,
      {
        type: SERVER.BooksDBConstants.LIT_RECEIVABLE_DEBIT,
        subType: SERVER.BooksDBConstants.LIS_RECEIVABLE_ITEM_CHARGE,
        referenceItemID: referenceItemID,
        amount: amount,
        name: entityName || $ctrl.receipt.receiptFrom,
        entityID: entityID || $ctrl.receipt.entityID
      }
    );
  }

  /*****************************************************************************
   * generateItemDebits
   */
  /*****************************************************************************
   * Generates the list of receivable charges for the payment sale items if
   * needed.
   ****************************************************************************/
  function generateItemDebits() {
    /*-------------------------------------------*/
    /* Create the charges for the receipt items. */
    /*-------------------------------------------*/
    paymentSaleItems().forEach(function (dgItem) {
      var src = dgItem.source();
      src.receivable =
        src.amount != 0
          ? generateItemCharge(src.id, src.amount, src.entityID, src.name)
          : null;
    });
  }

  /***************************************************************************
   * getReceipt */
  /**
* Loads a receipt from the database into the UI and returns resolved promise 
* if successful. Loads receipt for `id` if `dir` is null. Else, Next/previous 
* receipt from `id` is loaded.

* @param  {Number}  id  Receipt ID.
* @param  {int}     dir Direction, 0/null=current, >0=next, <0=previous.
* @return {Promise}     Resolves if successful, rejects if no receipt found.
***************************************************************************/
  function getReceipt(id, dir) {
    return $q(function (resolve, reject) {
      BusyScreen.setBusy(true);
      ReceiptsService.getReceipt(id, dir)
        .then(function (receipt) {
          return receipt && receipt.id ? $q.resolve(receipt) : $q.reject();
        })
        .then(loadReceipt, reject)
        .then(resolve)
        .finally(function () {
          BusyScreen.setBusy(false);
        });
    });
  }

  /*****************************************************************************
   * handleVoid
   */
  /*****************************************************************************
   * Handles the voiding/dropping of the current receipt.
   ****************************************************************************/
  function handleVoid() {
    if (!$ctrl.showVoid()) return;

    Alert.clear();

    if ($ctrl.receipt.credited) {
      Alert.info(
        'This receipt has been partially paid or discounted and cannot be voided',
        'Partially Paid'
      );
      return;
    }

    /*-------------------------------------------------*/
    /* If the receipt has no journal entry (Does not */
    /* affect the books), then this is a drop request. */
    /*-------------------------------------------------*/

    /*-------------------------------------------------------------*/
    /* Removing the drop receipt functionality, see BOOKS-412 for  */
    /* details. Commenting out the functionality as it is possible */
    /* that we will be asked to put this back in at some point.    */
    /*-------------------------------------------------------------*/
    //if (!$ctrl.receipt.hasJournalItems)
    //  {
    //  Alert.confirm("Dropping this receipt cannot be undone. Are you sure you want to continue?", "Confirm Drop")
    //    .then(function()
    //    {
    //    BusyScreen.setBusy(true);
    //    ReceiptsService.dropReceipt($ctrl.receipt.id).then(function()
    //      {
    //      BusyScreen.setBusy(false);
    //      Alert.success("The receipt has been successfully dropped.", "Receipt Dropped", true).then(function()
    //        {
    //      startNewReceipt(true);
    //        });
    //        }, function(resp)
    //        {
    //        BusyScreen.setBusy(false);
    //        Alert.error(resp.parsedResponse.message() || resp.statusText, "Drop Error");
    //        });
    //    });

    //  return;
    //  }

    /*----------------------------------------------------*/
    /* Otherwise this is a request to void/unvoid.        */
    /* Voiding the receipt shouldn't be seen as a change. */
    /*----------------------------------------------------*/
    var voidReceipt = function (vi) {
      return ReceiptsService.voidReceipt(vi);
    };
    VoidEntryDialog.show($ctrl.receipt, voidReceipt).then(function (result) {
      mUI_Flags.isVoided = result == VoidEntryDialog.STATUS_VOID;
      $ctrl.status = mUI_Flags.isVoided ? mStatusConfig.voided : null;

      /*---------------------------------------------------*/
      /* Receipt has been updated, reset the modified flag */
      /*---------------------------------------------------*/
      setModified(false);
    });
  }

  /*****************************************************************************
   * loadNextNumbers
   */
  /*****************************************************************************
   * Loads the next available receipt numbers
   *
   * @return Promise that is resolved after data has been set
   ****************************************************************************/
  function loadNextNumbers() {
    return BooksSvc.getNextReceiptNumbers().then(function (resp) {
      $ctrl.data.dbNextRcptNumHandwritten = resp.data.handNum;
      $ctrl.data.dbNextRcptNumPrinted = resp.data.printedNum;
    });
  }

  /*****************************************************************************
   * loadReceipt */
  /**
   * Load the provided receipt object into the app for review/management.
   *
   * @param  rcpt  Receipt object to load.
   * @return       Promise that resolves when the receipt has been loaded
   *****************************************************************************/
  function loadReceipt(rcpt) {
    /*---------------------------*/
    /* Clear the current watcher */
    /*---------------------------*/
    if (mReceiptWatcher) {
      mReceiptWatcher();
      mReceiptWatcher = null;
    }

    /*-------------------*/
    /* Reset Item Inputs */
    /*-------------------*/
    $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_PRODUCT);
    $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_ACCOUNT);
    $ctrl.notesCollapsed = true;

    /*------------------------------*/
    /* Set the receipt on the scope */
    /*------------------------------*/
    $ctrl.receipt = rcpt;

    /*------------------*/
    /* Clean up the UI. */
    /*------------------*/
    $ctrl.indicators = null;
    $ctrl.rcptGrid.data = [];
    $ctrl.payGrid.data = [];

    /*-----------------------------------*/
    /* Clear alerts */
    /*-----------------------------------*/
    Alert.clear();

    /*------------------------------------------------------------*/
    /* Reset the valGroup if it is ready. The object may not have */
    /* been set up yet in Firefox, but it won't be needed until   */
    /* the user tries to save, or manually starts a new receipt.  */
    /* By that point valGroup should be available in all cases.   */
    /*                                                            */
    /* Ticket: https://graystep.atlassian.net/browse/REQUEST-99   */
    /*------------------------------------------------------------*/
    var checkLength = Object.keys($ctrl.valGroup).length;

    // If there are no keys within val group it has no loaded yet so do not reset.
    if (checkLength > 0) $ctrl.valGroup.reset();

    mUI_Flags.isDeposited = !!rcpt.deposited;
    mUI_Flags.isSavedAsHand =
      !!$ctrl.receipt.handwritten && !mUI_Flags.isNewReceipt;
    mUI_Flags.isVoided = !!rcpt.voided;
    mUI_Flags.isWebStore = !!rcpt.isWebStore;
    mReceiptID = rcpt.id;
    $ctrl.showPrint = !rcpt.isNew;

    /*---------------------------*/
    /* Set receipt status object */
    /*---------------------------*/
    $ctrl.status = rcpt.cleared
      ? mStatusConfig.cleared
      : rcpt.deposited
        ? mStatusConfig.deposited
        : rcpt.voided
          ? mStatusConfig.voided
          : null;

    $ctrl.returnStatus = rcpt.returned
      ? mStatusConfig.returned
      : rcpt.partiallyReturned
        ? mStatusConfig.partiallyReturned
        : null;

    /*------------------------------------*/
    /* Initialize number if a new receipt */
    /*------------------------------------*/
    if (!rcpt.id)
      rcpt.receiptNumber = rcpt.handwritten
        ? $ctrl.data.dbNextRcptNumHandwritten
        : $ctrl.data.dbNextRcptNumPrinted;

    const itemReturnColumnIndex = $ctrl.rcptGrid.columnDefs.findIndex(
      (cd) => cd.field === 'returnedAmount'
    );

    /**
     * Only show the return amount column if the receipt is partially returned
     * or is returned.
     */
    if ($ctrl.receipt.partiallyReturned || $ctrl.receipt.returned) {
      const itemTotalColumnIndex = $ctrl.rcptGrid.columnDefs.findIndex(
        (cd) => cd.field === 'amount'
      );

      /*--------------------*/
      /* Item Return Amount */
      /*--------------------*/
      if (itemReturnColumnIndex === -1) {
        $ctrl.rcptGrid.columnDefs.splice(itemTotalColumnIndex + 1, 0, {
          field: 'returnedAmount',
          displayName: 'Returned',
          maxWidth: 90,
          enableSorting: false,
          precision: 2,
          allowNegative: false,
          cellFilter: 'currency'
        });
      }
    } else {
      if (itemReturnColumnIndex != -1) {
        $ctrl.rcptGrid.columnDefs.splice(itemReturnColumnIndex, 1);
      }
    }

    /*--------------------------------------------------------------*/
    /* Update Received From. If the entityID is provided, load the */
    /* corresponding addressEntityListItem, otherwise just set the */
    /* receiptFrom text to the model as it could be set without an */
    /* entityID. $q.when is used to ensure the entity data has been */
    /* loaded before the rest of the processing should continue */
    /*--------------------------------------------------------------*/
    return $q
      .when(
        rcpt.entityID
          ? $ctrl
              .getAddressEntityListItem(rcpt.entityID)
              .then(function (entity) {
                return selectEntity(entity, true);
              })
          : ($ctrl.data.from = rcpt.receiptFrom)
      )
      .then(function () {
        mUI_Flags.isPickupReceipt = false;

        /*--------------------------------------*/
        /* Add the receiptItems to the datagrid */
        /* and check for any pickup items */
        /*--------------------------------------*/
        for (var i = 0; i < rcpt.receiptItems.length; i++) {
          $ctrl.addReceiptItem(rcpt.receiptItems[i], true);

          if (
            rcpt.receiptItems[i].type == SERVER.BooksDBConstants.LIT_ITEM_PICKUP
          )
            mUI_Flags.isPickupReceipt = true;
        }

        /*-----------------------------*/
        /* Update hasReturns indicator */
        /*-----------------------------*/
        mUI_Flags.hasReturns = $ctrl.rcptGrid.data.some(function (dgItem) {
          return dgItem.hasReturns();
        });

        /*-------------------------------------*/
        /* Update hasDestinyPayment indicator. */
        /*-------------------------------------*/
        mUI_Flags.hasDestinyPayment = $ctrl.rcptGrid.data.some(
          function (dgItem) {
            return dgItem.isDestinyPayment();
          }
        );

        /*-----------------------*/
        /* Process payment items */
        /*-----------------------*/
        mOriginalPaidTotal = 0;
        mPayPalAmount = 0;
        mReceivableCharge = rcpt.receivableCharge || 0;
        mSavedFunds = null;

        /*----------------------------------*/
        /* Process and convert PaymentItems */
        /*----------------------------------*/
        $ctrl.payGrid.data = (rcpt.paymentItems || [])
          .map(function (pi) {
            /*-------------------------------------------------------*/
            /* For a webstore receipt, the paypal item will show the */
            /* full amount. This will not be an issue with editting */
            /* as web store receipts are currently not edittable. */
            /* When this changes, this will have to be readdressed. */
            /*-------------------------------------------------------*/
            if (
              pi.subType == SERVER.BooksDBConstants.LIS_WEBSTORE &&
              pi.processorType == SERVER.BooksDBConstants.PT_PAY_PAL
            ) {
              mPayPalAmount = pi.amount;
              pi.amount = rcpt.amount;
            }

            mOriginalPaidTotal += pi.amount;

            /*------------------------------------------------*/
            /* Store existing student funds debit information */
            /*------------------------------------------------*/
            if (pi.type == SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_DEBIT)
              mSavedFunds = { entityID: pi.entityID, amount: pi.amount };

            return new Data.DGPaymentItem(pi);
          })
          .sort(function (a, b) {
            /*----------------------*/
            /* Put Cash at the top. */
            /*----------------------*/
            if (a.typeString == 'CASH') return -1;
            if (b.typeString == 'CASH') return 1;

            /*------------------------*/
            /* Add Cards, then Funds. */
            /*------------------------*/
            if (a.typeString == 'CARD' && b.typeString == 'FUNDS') return -1;
            if (a.typeString == 'FUNDS' && b.typeString == 'CARD') return 1;

            /*---------------------------*/
            /* Put Checks at the bottom. */
            /*---------------------------*/
            if (a.typeString == 'CHECK') return 1;
            if (b.typeString == 'CHECK') return -1;

            return a.typeString.localeCompare(b.typeString);
          });

        mOriginalPaidTotal = Tools.roundMoney(mOriginalPaidTotal);

        /*-------------------------------------------------------*/
        /* Set flags. Credit/Debit cards are marked as cleared */
        /* when they are part of a reconciliation or when they */
        /* are part of a voided transaction. Thus if the receipt */
        /* is voided, the cleared indication will not matter. */
        /*-------------------------------------------------------*/
        mUI_Flags.hasClearedCard = (rcpt.paymentItems || []).some(
          function (pi) {
            return (
              [
                SERVER.BooksDBConstants.LIS_CREDIT_CARD,
                SERVER.BooksDBConstants.LIS_DEBIT_CARD
              ].indexOf(pi.subType) > -1 &&
              pi.isCleared &&
              !mUI_Flags.isVoided
            );
          }
        );

        /*--------------------------------------------*/
        /* Set the unvoidable flag condition based on */
        /* referencing documents (Deposits/Recons) or */
        /* if there have been payments\credits */
        /*                                            */
        /* Payments/credits made on OTHER receipts    */
        /* are rcpt.credited (dbo.ReceiptStatus). A   */
        /* receipt with unpaid items and nothing else */
        /* attached is voidable - the server         */
        /* (books.AdjustReceivablesForEntry) refuses  */
        /* the void with the receipt # if anything    */
        /* has been attached since. The old "paid +   */
        /* receivable charge < item payments" check   */
        /* was removed: it was a receipt-level        */
        /* receivables leftover that could never see  */
        /* another receipt's payments, and it hid     */
        /* Void on unpaid kit receipts.               */
        /*--------------------------------------------*/
        mUI_Flags.unvoidable =
          mUI_Flags.isDeposited ||
          mUI_Flags.isPickupReceipt ||
          mUI_Flags.hasClearedCard ||
          !!rcpt.credited ||
          !!rcpt.hasPickups ||
          mUI_Flags.isWebStore ||
          mUI_Flags.hasReturns ||
          mUI_Flags.hasDestinyPayment ||
          mUI_Flags.hasKpsPayment ||
          /*
           * This is a return receipt with returned card charges
           */
          (mUI_Flags.isReturnReceipt &&
            (rcpt.paymentItems || []).some(function (pi) {
              return (
                [
                  SERVER.BooksDBConstants.LIS_CREDIT_CARD,
                  SERVER.BooksDBConstants.LIS_DEBIT_CARD
                ].indexOf(pi.subType) > -1 && pi.cardData
              );
            }));

        /*--------------------------------------------------*/
        /* If the receipt can be edited, expand the */
        /* appropriate tab based on the openAccountTab flag */
        /*--------------------------------------------------*/
        $ctrl.productsCollapsed =
          !$ctrl.allowItemUpdates() || mAppConfig.openAccountTab;
        $ctrl.accountsCollapsed =
          !$ctrl.allowItemUpdates() || !mAppConfig.openAccountTab;

        /*----------------------------*/
        /* Set scroll to top of page. */
        /*----------------------------*/
        window.scrollTo(0, 0);

        /*---------------------------------------------------*/
        /* Add the modified property as un-enumerable so the */
        /* watch will ignore it in its checks for equality */
        /*---------------------------------------------------*/
        Object.defineProperty($ctrl.receipt, 'modified', {
          enumerable: false,
          writable: true,
          value: false
        });

        /*---------------------------------------------------*/
        /* Start watching for changes, only setting the */
        /* flag when the previous value has been established */
        /*---------------------------------------------------*/
        mReceiptWatcher = $scope.$watch(
          'receipt',
          function () {
            setModified(true);
          },
          true
        );

        /*--------------------------------------------------------*/
        /* Clear the modified property after the next digest */
        /* cycle as it was set during the initialization of watch */
        /*--------------------------------------------------------*/
        $timeout(setModified);

        /*-----------------------------------------------------------*/
        /* Set the focus on page load. Focus will be on the date if */
        /* the date is null, otherwise it will be on the From input. */
        /*-----------------------------------------------------------*/
        document
          .getElementById(mAppConfig.defaultDate ? 'receiptFrom' : 'date')
          .focus();

        BusyScreen.setBusy(false);
      });
  }

  /*****************************************************************************
   * printReceipt
   */
  /*****************************************************************************
   * Checks if a modified receipt should be printed, otherwise generate the PDF.
   *
   * @return Promise that is resolved when the receipt print dialog is closed
   ****************************************************************************/
  function printReceipt() {
    /*------------------------------------------------------------------*/
    /* If there are unsaved changes to the receipt, prompt for printing */
    /*------------------------------------------------------------------*/
    if ($ctrl.receipt.modified)
      return Alert.confirm(
        'There have been changes to this receipt. Would you like to print the unmodified version?',
        'Modified Receipt'
      ).then(function () {
        return printReceiptPDF($ctrl.receipt.id);
      });
    else return printReceiptPDF($ctrl.receipt.id);
  }

  /*****************************************************************************
   * printReceiptPDF
   */
  /*****************************************************************************
   * Prints the receipt and displays an alert.
   *
   * @param receiptID
   *          Receipt to print PDf for.
   * @return Promise that is resolved when the alert is closed
   ****************************************************************************/
  function printReceiptPDF(receiptID) {
    window.open('PrintReceiptPDF.aspx?receiptID=' + receiptID, '_blank');

    return Alert.info(
      'This receipt has been created in a separate window as a PDF.\n\n' +
        'Click Ok when Finished Printing',
      'Print Receipt',
      true
    );
  }

  /*****************************************************************************
   * productLabelFunction
   */
  /*****************************************************************************
   * Executes the column field value as a function to retrieve the value.
   *
   * @param data
   *          DGReceiptItem to display
   * @param col
   *          UIGrid Column reference
   * @return Value field string
   ****************************************************************************/
  function productLabelFunction(data, col) {
    return data[col.field]();
  }

  /*****************************************************************************
   * receiptsFound
   */
  /*****************************************************************************
   * Returns an indication that there are multiple rece,ipts found.
   ****************************************************************************/
  function receiptsFound() {
    return FindReceiptDialog.foundReceipts.length > 1;
  }

  /*****************************************************************************
   * removeItem
   */
  /*****************************************************************************
   * Removes the provided DGReceiptItem from the receipt item grid. If this is a
   * group, the sub items will be removed as well.
   *
   * @param item
   *          DGReceiptItem to remove
   ****************************************************************************/
  function removeItem(item) {
    var removeIdx = $ctrl.rcptGrid.data.indexOf(item);
    $ctrl.rcptGrid.data.splice(removeIdx, 1);

    if (item.subItems) item.subItems.forEach(removeItem);
  }

  /*****************************************************************************
   * savePrecheck
   */
  /*****************************************************************************
   * This is called as a result of the save button being clicked. This will
   * verify the model is valid and determine if there will be any balances put
   * on account. Afterwhich the save routine will be called.
   ****************************************************************************/
  function savePrecheck() {
    // Make sure to clear validation pop up message so the user does not get confused.
    $ctrl.valGroup.reset();
    Alert.clear();

    if ($ctrl.receipt.entryDate < new Date(1753, 1, 1))
      return Alert.error('Invalid date');
    /*--------------------------------------*/
    /* Ensure there is at least one Account */
    /* or Receipt Item before saving.       */
    /*--------------------------------------*/
    if (!$ctrl.rcptGrid.data.length) {
      Alert.error('At least one line item is required.', 'Missing Items', {
        hide: true
      });
      return true;
    }

    /*---------------------------------------------*/
    /* Validates the date and Number are populated */
    /*---------------------------------------------*/
    if (!$ctrl.valGroup.validate()) return;

    /*-----------------------------------------------------*/
    /* Verify total amount on a receipt is greater than 0. */
    /*-----------------------------------------------------*/
    if (!mUI_Flags.isReturnReceipt && $ctrl.salesTotal() < 0) {
      Alert.error('Receipt must have a positive balance', 'Invalid Total', {
        hide: true
      });
      return;
    }

    /*-------------------*/
    /* Validate Payments */
    /*-------------------*/
    if (
      $ctrl.payGrid.data.some(function (dgPayment) {
        if (
          (dgPayment.isCheck() || dgPayment.isCard()) &&
          (!dgPayment.number || !dgPayment.name)
        ) {
          Alert.error(
            'Name and Number are required for ' +
              dgPayment.typeString.toLowerCase() +
              's',
            'Missing Info',
            { hide: true }
          );
          return true;
        }

        if (
          dgPayment.isFundDebit() &&
          dgPayment.amount > $ctrl.studentFunds()
        ) {
          Alert.error(
            'Funds used cannot exceed ' +
              $filter('currency')($ctrl.studentFunds()),
            'Insufficient Funds',
            { hide: true }
          );
          return true;
        }

        if (!dgPayment.amount || isNaN(dgPayment.amount)) {
          Alert.error('Payments require an amount.', 'Invalid Payments', {
            hide: true
          });
          return true;
        }

        return false;
      })
    )
      return;

    /*------------------------------------------------------------------------------------------------------------------*/
    /* We use the function, paymentTotal(), to grab the most recent total whenever a new item is added or removed so    */
    /* that we can use this value to do our various checks                                                              */
    /*------------------------------------------------------------------------------------------------------------------*/
    var paymentTotal = $ctrl.paymentTotal();

    /*------------------------------------------------------------------------------------------------------------------------------*/
    /* This check is important as it will ensure that the mOriginalPaidTotal (whatever amount that was first saved when the receipt */
    /* was created) remains the same. Meaning if the user adds more payment items, resulting in a new paymentTotal, it will NOT     */
    /* allow them to add onto the original Receipt because the mOriginalPaidTotal and paymentTotal will now be different.           */
    /* This is intended behavior as we do not want the original paid total being changed. Users will still be able to add memo.     */
    /*------------------------------------------------------------------------------------------------------------------------------*/
    if (!mUI_Flags.isNewReceipt && mOriginalPaidTotal != paymentTotal) {
      Alert.error(
        'The Payment Line Item(s) Total does not match the original payment total.',
        'Payments Out of Balance'
      );
      return;
    } else {
      /*------------------------------------------------------*/
      /* Otherwise the data is valid and we need to determine */
      /* if any balances need/can be put on account. */
      /*------------------------------------------------------*/
      var balance = amtRemaining();
      var adjustedPaid = Tools.roundMoney(paymentTotal + mReceivableCharge);
      var isFullyPaid = balance <= 0;
      var nonPaymentList = nonPaymentItems();
      var paymentSaleList = paymentSaleItems();

      var nonPaymentTotal = 0;
      nonPaymentList.forEach(function (dgItem) {
        nonPaymentTotal = Tools.roundToPrecision(
          nonPaymentTotal + dgItem.amount,
          2
        );
      });

      /*--------------------------------------*/
      /* Check if items can be put on account */
      /*--------------------------------------*/
      if (!(isFullyPaid || mAppConfig.allowOnAccount)) {
        Alert.info(
          'The receipt total has not been fully paid. Please enter the payment(s) for the remaining amount.',
          'Balance Due'
        );
        return;
      }

      /*----------------------------------------------------------*/
      /* Ensure the minimum due has been met. Include the receipt */
      /* charge amount in the total to compare for old receipts. */
      /*----------------------------------------------------------*/
      if (
        !mUI_Flags.isReturnReceipt &&
        adjustedPaid < nonPaymentTotal &&
        mAppConfig.allowOnAccount
      ) {
        Alert.info(
          'A minimum of {0} is due for donations or items that do not allow payments.'.format(
            $filter('currency')(nonPaymentTotal)
          ),
          'Minimum Balance Due'
        );
        return;
      }

      /*------------------------------------------------------------------------------------------------*/
      /* If the receipt isn't paid in full we need to make sure every item that allows payments (and so */
      /* can carry a receivable) can have its debt properly assigned to an entity.                      */
      /*                                                                                                 */
      /* 1. Since non-payment items are guaranteed to be covered (checked above), only payment sale      */
      /* items can end up with an outstanding balance.                                                   */
      /* 2. Ignore any of those items that already have an internal entity assigned directly on the      */
      /* item, those items are already properly assigned and do not need the From field as a backup.     */
      /* 3. The From field can only act as a valid backup for the rest if it points to a real internal   */
      /* entity - an outside entity (a typed name with no id) has no entity id to attach a debt to.      */
      /* 4. If any item still can not have its debt assigned, tell the user which item(s) need someone   */
      /* assigned instead of just a generic "populate the From field" message.                           */
      /*------------------------------------------------------------------------------------------------*/
      if (!isFullyPaid) {
        var unassignedPaymentItems = paymentSaleList.filter(function (dgItem) {
          var src = dgItem.source();
          return src.amount != 0 && !src.entityID;
        });

        if (unassignedPaymentItems.length > 0 && !$ctrl.receipt.entityID) {
          unassignedPaymentItems.forEach(function (dgItem) {
            Alert.info(
              'The item "{0}" has a remaining balance and needs an individual assigned, or the From field populated with an internal individual.'.format(
                dgItem.description()
              ),
              'Anonymous Receipt'
            );
          });
          document.getElementById('receiptFrom').focus();
          return;
        }
      }

      // $scope.receiptWatcher.stop();
      /*--------------------------------------------------------*/
      /* Do a normal save for a fully paid receipt or a */
      /* receipt that has a charge amount for the full balance. */
      /*--------------------------------------------------------*/
      if (
        isFullyPaid ||
        Tools.roundToPrecision(balance - mReceivableCharge, 2) == 0
      )
        saveReceipt();
      /*----------------------------------------------------------*/
      /* Generate full debit amounts to all items if nothing has */
      /* been paid or if the nonPayment total has been fulfilled. */
      /*----------------------------------------------------------*/ else if (
        adjustedPaid == 0 ||
        adjustedPaid == nonPaymentTotal
      ) {
        generateItemDebits();
        saveReceipt();
      } else if (
        /*----------------------------------------------*/
        /* For a single payment item, generate the */
        /* debit for the item amount less the cash paid */
        /*----------------------------------------------*/
        adjustedPaid > nonPaymentTotal &&
        paymentSaleList.length == 1
      ) {
        var ri = paymentSaleList[0].source();
        var debitAmt = Tools.roundToPrecision(
          ri.amount - (adjustedPaid - nonPaymentTotal),
          2
        );
        ri.receivable = generateItemCharge(
          ri.id,
          debitAmt,
          ri.entityID,
          ri.name
        );
        saveReceipt();
      } else if ($ctrl.payGrid.data.length > 0) {
        /*-----------------------------------------------------------*/
        /* No automated distribution can be done, bring up the */
        /* panel to distribute any payments across the payment items */
        /*-----------------------------------------------------------*/
        var distributionTotal = Tools.roundToPrecision(
          paymentTotal + mReceivableCharge - nonPaymentTotal,
          2
        );
        var receiptItems = paymentSaleList.map(function (dgItem) {
          return dgItem.source();
        });

        PaymentDistributionDialog.show(
          receiptItems,
          $ctrl.data.from.entity,
          distributionTotal
        ).then(function () {
          saveReceipt();
        });
      }
    }
  }

  /*****************************************************************************
   * saveReceipt
   */
  /*****************************************************************************
   * This will perform limiter validation before sending a shallow copy of the
   * scope.receipt to the server and reset the UI on save completion.
   ****************************************************************************/
  function saveReceipt(ignoreConfirm) {
    /*-----------------------------------------*/
    /* Indication to print receipt after save. */
    /*-----------------------------------------*/
    var doPrint = !$ctrl.receipt.handwritten;

    /*-------------------------------*/
    /* Save routine for the receipt. */
    /*-------------------------------*/
    var save = function () {
      BusyScreen.setBusy(true);

      /*---------------------------------------------*/
      /* The data.from could be a string from a From */
      /* with no entityID as it is allowed from POS. */
      /*---------------------------------------------*/
      var fromIsName = $ctrl.data.from && angular.isString($ctrl.data.from);
      var fromIsObject = $ctrl.data.from && angular.isObject($ctrl.data.from);

      /*--------------------------------------------------*/
      /* Make copy of receipt for sending to the service. */
      /*--------------------------------------------------*/
      var receipt = angular.extend(
        {
          entityID: fromIsObject ? $ctrl.data.from.entity.id : 0,
          receiptFrom: fromIsName
            ? $ctrl.data.from
            : fromIsObject
              ? $ctrl.data.from.entity.name
              : null,
          type: $ctrl.receipt.type || SERVER.BooksDBConstants.LET_RECEIPT,
          paymentItems: [],
          source: $ctrl.receipt.source || SERVER.BooksDBConstants.RS_WEBAPP,
          amount: $ctrl.salesTotal()
        },
        $ctrl.receipt
      );

      /*----------------------------------*/
      /* Set the items from the rcptGrid. */
      /*----------------------------------*/
      receipt.receiptItems = $ctrl.rcptGrid.data
        .filter(function (dgItem) {
          return !dgItem.isChildItem();
        })
        .map(function (dgItem) {
          return dgItem.source();
        });

      /*-------------------------------------------------------*/
      /* Add Payments by making a copy as the web payment item */
      /* will have its amount updated so if there was a server */
      /* validation error the wrong amount would display */
      /*-------------------------------------------------------*/
      receipt.paymentItems = $ctrl.payGrid.data.map(function (dgPayment) {
        var pi = angular.extend({}, dgPayment.source());

        /*-------------------------------------------*/
        /* Update the account if this is a new item. */
        /*-------------------------------------------*/
        if (!pi.id) {
          pi.accountID = dgPayment.isCard()
            ? mManualProcessor.merchantAcctID
            : dgPayment.isFunds()
              ? 0 // Student Funds account assigned on server.
              : mUFAccountID;
        }

        /*--------------------------------------------*/
        /* If this is a paypal payment, set the */
        /* original amount before the save is called. */
        /*--------------------------------------------*/
        if (dgPayment.isWeb()) pi.amount = mPayPalAmount;

        return pi;
      });

      /*-------------------*/
      /* Save the receipt. */
      /*-------------------*/
      ReceiptsService.saveReceipt(receipt).then(
        function (data) {
          BusyScreen.setBusy(false);

          /*----------------------------------------------------------------*/
          /* Show change due for a new receipt only. We must set showChange */
          /* before the receipt ID is set to ensure the flag is accurate. */
          /*----------------------------------------------------------------*/
          var showChange = mUI_Flags.isNewReceipt;

          /*--------------------------------*/
          /* Update the receipt and finish. */
          /*--------------------------------*/
          $ctrl.receipt = receipt;
          $ctrl.receipt.id = data.id;

          /** Utility function to finish the save process. */
          var onSave = function () {
            $ctrl.receipt.modified = false;
            return (
              $q
                .when(doPrint ? printReceipt() : null)
                /**
                 * We use a .finally here because the printReceipt shows an alert and whether or not the user hits "Ok"
                 * in the alert or clicks off the modal we want to start a new receipt. This is why we use a .finally.
                 */
                .finally(function () {
                  return startNewReceipt(true);
                })
            );
          };

          if (showChange && amtRemaining() < 0) {
            Alert.info(
              $filter('currency')(Math.abs(amtRemaining())) + ' Due',
              'Change Due',
              true
            ).finally(onSave);
          }
          // Since we use a .finally above when we get a response from print receipt we need a .finally here to catch the response of the .finally above.
          else
            onSave().finally(function () {
              Alert.success('Receipt Saved', 'Write Receipts');
            });
        },
        function (resp) {
          BusyScreen.setBusy(false);
          Alert.error(
            resp.parsedResponse.message() || resp.statusText,
            'Save Error'
          );
        }
      );
    };

    /*-----------------------------------------------------------------*/
    /* Check to see if the student funds payment item exceeds the      */
    /* sales total. There will only be one Student Funds payment item. */
    /*-----------------------------------------------------------------*/
    var studentFundsItem = $ctrl.payGrid.data
      .filter(function (dgItem) {
        return dgItem.isFundDebit();
      })
      .shift();
    if (studentFundsItem) {
      /*-------------------------------------------------*/
      /* If Student Funds are being used, don't allow    */
      /* the student funds total exceed the sales total. */
      /*-------------------------------------------------*/
      if (studentFundsItem.amount > $ctrl.salesTotal()) {
        Alert.error('Cannot overpay using Student Funds.', 'Save Error');
        return;
      }
    }

    /*----------------------------------------------*/
    /* Validate sales limiters for sale/order items */
    /*----------------------------------------------*/
    var receiptItems = $ctrl.rcptGrid.data
      .filter(function (dgItem) {
        return (
          delete dgItem.source().limiters,
          dgItem.isSaleOrder() && !dgItem.isChildItem()
        );
      })
      .map(function (dgItem) {
        return dgItem.source();
      });

    validateLimiterRestrictions(receiptItems).then(function (needConfirm) {
      /*-------------------------------------------*/
      /* Call save if ignoring the confirmation or */
      /* on the yes option from the confirmation. */
      /*-------------------------------------------*/
      if (!needConfirm || ignoreConfirm) save();
      else
        Alert.confirm(
          'This will save' +
            (doPrint ? ' and print' : '') +
            ' this receipt.\n\nDo you want to continue?',
          'Confirm Save'
        ).then(save);
    });
  }

  /*****************************************************************************
   * selectEntity
   */
  /*****************************************************************************
   * Set the AddressEntityListItem entity on the scope from field and update
   * model data accordingly.
   *
   * @param entity
   *          AddressEntityListItem to select.Null=clear,
   * @param skipEntityDialog
   *          Skip auto-showing of entity info dialog
   ****************************************************************************/
  function selectEntity(entity, skipEntityDialog) {
    if (!$ctrl.receipt) return;
    $ctrl.data.from = entity;

    if (entity != mSavedEntity) {
      mSavedEntity = entity;
      applyDiscount();
      $ctrl.indicators = null;
      $ctrl.receipt.receiptFrom = mSavedEntity
        ? mSavedEntity.entity.name
        : null;
      $ctrl.receipt.entityID = mSavedEntity ? mSavedEntity.entity.id : 0;

      /*-------------------------------------*/
      /* Remove any student fund debit items */
      /* that are for a different entity */
      /*-------------------------------------*/
      $ctrl.payGrid.data
        /** Find fund debit for different entity. */
        .filter(function (dgPayment) {
          return (
            dgPayment.isFundDebit() &&
            (!mSavedEntity ||
              mSavedEntity.entity.id != dgPayment.source().entityID)
          );
        })

        /** Remove from list. */
        .forEach(function (dgPayment) {
          deletePayment(dgPayment);
        });

      /*----------------------------------------------------------------------*/
      /* Retrieve indicators and current student fund balance. These will be */
      /* fired off seperately as they don't rely on the other being available */
      /*----------------------------------------------------------------------*/
      if (mSavedEntity)
        return ReceiptsService.getStudentBalanceAndIndicators(
          mSavedEntity.entity.id
        ).then(function (data) {
          $ctrl.indicators = data.indicators;
          $ctrl.data.from.funds = data.balance;

          if (!skipEntityDialog) entityInfo(mSavedEntity, false);
        });
    }
  }

  /*****************************************************************************
   * setModified
   */
  /*****************************************************************************
   * Set the modified property on the $ctrl.receipt. If mReceiptWatcher has not
   * been instantiated, then there is no watching being done, so the modified
   * property will not be updated.
   *
   * @param modified
   *          Boolean indication that the receipt has been modified.
   ****************************************************************************/
  function setModified(modified) {
    if (!mReceiptWatcher) return;

    $ctrl.receipt.modified = !!modified;
  }

  /*****************************************************************************
   * startNewReceipt */
  /**
   * Loads a new receipt object to a manage. If reloadLists is true, the list
   * data will be loaded prior to the receipt.
   *
   * This returns a promise that can be listened to for when the UI is ready.
   *
   * @param  reloadLists  Indication to reload the data lists before
   *                      loading in a new receipt
   * @return              Promise that resolves when the UI is ready
   *****************************************************************************/
  function startNewReceipt(reloadLists) {
    return $q.when(reloadLists ? loadNextNumbers() : null).then(function () {
      /*--------------------------*/
      /* Clear find receipts data */
      /*--------------------------*/
      mCurrentIdx = -1;
      FindReceiptDialog.reset();

      /*----------------------*/
      /* Load the new receipt */
      /*----------------------*/
      return loadReceipt({
        handwritten: mAppConfig.defaultHandwritten,
        entryDate: mAppConfig.defaultDate,
        receiptItems: [],
        paymentItems: [],
        entityID: 0,
        receiptFrom: null,
        isNew: true
      });
    });
  }

  /*****************************************************************************
   * validateLimiterRestrictions
   */
  /*****************************************************************************
   * Uses the LimitedReceiptItemsDialog to validate the limiters for the receipt
   * items. If the user accepts the limiters then the promise will resolve and
   * the normal app operation will continue. If the user does not accept the
   * limiters then the promise will reject and kick out.
   *
   * @param receiptItems
   *          List of ReceiptItem objects to validate.
   * @return Promise which will resolve when there are no limiters or the user
   *         acknowledges the limiters and wishes to continue.
   ****************************************************************************/
  function validateLimiterRestrictions(receiptItems) {
    /*---------------------------------------------------------*/
    /* Continue normal app operation if data cannot be changed */
    /*---------------------------------------------------------*/
    if (!$ctrl.allowItemUpdates()) {
      /**
       * We resolve true here so that the confirm modal will appear.
       */
      return $q.resolve(true);
    }

    /**
     * Builds a Map that returns the current payments by the entity id. This is valuable for the limiters
     * because we need to know the current payments for the entity to determine if the entity is going to trigger the
     * balance limiter for the item.
     */
    const paymentsByEntityID = $ctrl.rcptGrid.data.reduce(
      (paymentsList, dataGridItem) => {
        const entityID = dataGridItem.source().entityID;

        if (!dataGridItem.isPayment() || !entityID) return paymentsList;

        const currentPayments = paymentsList.get(entityID) || 0;

        paymentsList.set(
          entityID,
          Tools.roundMoney(currentPayments + dataGridItem.amount)
        );

        return paymentsList;
      },
      new Map()
    );

    return LimitedReceiptItemsDialog.show(
      receiptItems,
      paymentsByEntityID,
      $ctrl.receipt.entryDate || new Date()
    ).then((modalWasNotShown) => {
      /**
       * If the modalWasNotShown flag is true then the modal did not display to the user. This means
       * there were no item limiters found so we return the true modalWasNotShown flag which makes
       * it so the confirm modal will appear. If the modal was shown and the user hit the confirm/yes button
       * the modal returns the modalWasNotShown as null which is falsy and will make it so the confirm modal
       * will not appear. This is important because we do not want the user to have to accept 2 modals the standard
       * confirm and the limiter restrictions modal, if the user accepted the limiter restriction modal then we ignore
       * the confirm modal.
       */
      return $q.resolve(modalWasNotShown);
    });
  }

  /*****************************************************************************
   * validateSelection
   */
  /*****************************************************************************
   * The user can select the entity by name or barcode and may add a new entity.
   * If not an entity object, check the value to see if it is a studentID and
   * select the entity matching that studentID. If it is a Receipt barcode, we
   * need to try to open the entityInfo dialog to display any open orders
   * associated with that receipt.
   *
   * @param entity
   *          Validate the entry from the typeahead. If <b>entity</b> is an
   *          object, this is an actual entity. Otherwise, this is a string that
   *          we need to run through the BarCode validator.
   * @param input
   *          Input that triggered the blur
   ****************************************************************************/
  function validateSelection(entity, input) {
    $ctrl.validateEntity(entity, input).then(function (entityTemplate) {
      if (!entityTemplate) return;
      /*-------------------------------------------*/
      /* Select the retrieved entity, skip */
      /* auto-loading the entityInfo dialog if new */
      /*-------------------------------------------*/ else if (
        entityTemplate?.isEntity
      )
        selectEntity(entityTemplate.data, entityTemplate.add);
      /*-------------------------------------------*/
      /* Otherwise this is a parsed receipt number */
      /*-------------------------------------------*/ else
        entityInfo(entity, true);
    });
  }

  /*****************************************************************************
   * Controller Methods
   ****************************************************************************/
  /** Wrapper for scope event listening. */
  $ctrl.addEventListener = function (type, handler) {
    $scope.$on(type, handler);
  };

  /*****************************************************************************
   * addReceiptItem */
  /**
   * This is called when a ReceiptItem needs to be added to the receipt. This
   * will be called from the item input sections and when a receipt is loaded.
   * This is also used to handle editting.
   *
   * @param ri
   *          ReceiptItem to add/update
   * @param ignoreDiscount
   *          Indication to not apply discount logic
   *****************************************************************************/
  $ctrl.addReceiptItem = function (ri, ignoreDiscount) {
    /*----------------------------------------------*/
    /* Create new DGReceiptItem to potentially add. */
    /*----------------------------------------------*/
    var dgi = new Data.DGReceiptItem(ri);

    /*-----------------------------------------------------------------*/
    /* Set product source when applicable using the passed productList */
    /* as the primary search list otherwise use mProductList           */
    /*-----------------------------------------------------------------*/
    var setProduct = function (dgItem, productList) {
      /*-------------------------------------------------------*/
      /* Attempt to find the product if the item has an itemID */
      /*-------------------------------------------------------*/
      if (dgItem.itemID())
        dgItem.productSource =
          (productList || [])
            .filter(function (sp) {
              return sp.id == dgItem.itemID();
            })
            .shift() ||
          mProductList
            .filter(function (p) {
              return p.id == dgItem.itemID();
            })
            .shift();

      /*---------------------------------------------*/
      /* If there are subItems on the dgItem, update */
      /* their product source using this product's   */
      /* subItems list as the primary search list    */
      /*---------------------------------------------*/
      if (dgItem.subItems)
        dgItem.subItems.forEach(function (si) {
          setProduct(
            si,
            dgItem.productSource ? dgItem.productSource.subItems : null
          );
        });
    };

    setProduct(dgi);

    /*-----------------------------------------*/
    /* Utility method to add a DGReceiptItem   */
    /* and its subItems at the specified index */
    /*-----------------------------------------*/
    var addItem = function (item, addIndex) {
      $ctrl.rcptGrid.data.splice(addIndex, 0, item);

      /*------------------------------------*/
      /* Add the subitems at the next index */
      /*------------------------------------*/
      if (item.subItems)
        item.subItems.forEach(function (si, subIdx) {
          addItem(si, addIndex + subIdx + 1);
        });
    };

    /**
     * This is built to find a membership item in the datagrid that is associated with the same individual. This is built to handle the case where the user
     * is attempting to add a membership item to the cart and there is already a membership item in the cart for the same individual. This will find that
     * item and prevent the user from adding the same membership item to the cart.
     */
    const individualMembershipItem = dgi.isMembership()
      ? $ctrl.rcptGrid.data.find(
          (dgItem) =>
            dgItem.isMembership() &&
            dgi.isMembership() &&
            dgItem.source().entityID &&
            dgi.source().entityID &&
            dgItem.source().entityID === dgi.source().entityID
        )
      : null;

    /**
     * This is built to prevent the user from adding multiple of the same membership to the same individual in one transaction. This is built to handle the case where the user
     * tries to add a kit that has a membership in it and a membership item in the same transaction. Since that kit also has a membership in it, we want to prevent the user from adding
     * the membership item to the cart. This also prevents adding multiples of the same membership item to the cart.
     */
    if (individualMembershipItem) {
      Alert.error(
        `A membership for ${individualMembershipItem.source().entity.name} is already in the cart.`,
        'Write New Receipts'
      );

      return;
    }

    /*----------------------------------------------*/
    /* Check if there is a similar item already in  */
    /* the datagrid indicated by the same id (edit) */
    /* or by the equals method (update existing)    */
    /*----------------------------------------------*/
    if (
      $ctrl.rcptGrid.data.some(function (dgItem, idx) {
        /*-----------------------------------------------------------------*/
        /* Skip child items as they are processed by the parent group item */
        /*-----------------------------------------------------------------*/
        if (dgItem.isChildItem()) return false;

        /*-----------------------------------*/
        /* Same ids indicate an edit,        */
        /* remove the old item(s) and update */
        /*-----------------------------------*/
        if (dgItem.id() && dgItem.id() == dgi.id()) {
          /*---------------------*/
          /* Remove the old item */
          /*---------------------*/
          removeItem(dgItem);

          /*------------------*/
          /* Add the new item */
          /*------------------*/
          addItem(dgi, idx);
        } else if (dgItem.equals(dgi)) {
          /*---------------------------------------------------------*/
          /* The item to be added is equivalent to an existing       */
          /* item, update the existing with the new item information */
          /*---------------------------------------------------------*/
          /*----------------------------------------------------*/
          /* If the similar item has a quantity change, then    */
          /* the quantity cannot change so no update may happen */
          /*----------------------------------------------------*/
          if (dgItem.isSaleOrder()) {
            if (dgItem.hasQtyChange()) {
              Alert.info(
                'This item already exists in the list. This item does not allow multiple purchases for the same person.',
                'Write New Receipt'
              );
              return true;
            } else if (dgItem.isMembership()) {
              Alert.info(
                'This membership already exists in the list. You can not purchase multiple of the same membership for the same person.',
                'Write New Receipt'
              );
              return true;
            }
          }

          dgItem.increase(dgi);
        } else return false;

        /*-------------------------------------*/
        /* Otherwise the items are not similar */
        /*-------------------------------------*/

        /*-----------------------------------------------*/
        /* An item was updated, update the modified flag */
        /*-----------------------------------------------*/
        setModified(true);
        return true;
      })
    )
      return;

    /*-----------------------------------*/
    /* If no match found, add a new row. */
    /*-----------------------------------*/
    addItem(dgi, $ctrl.rcptGrid.data.length);
    setModified(true);

    /*--------------------------------------------*/
    /* Update discount information if not ignored */
    /*--------------------------------------------*/
    if (!ignoreDiscount) applyDiscount();
  };

  /*****************************************************************************
   * getAddressEntityListItem
   */
  /*****************************************************************************
   * Load the AddressEntityListItem record for the provided <b>id</b>.
   *
   * @param id
   *          Entity ID to search against.
   * @return Promise that resolves with corresponding AddressEntityListItem
   *         object.
   ****************************************************************************/
  $ctrl.getAddressEntityListItem = function (id) {
    return BooksSvc.getAddressEntityListItem(id).then(function (resp) {
      return resp.data;
    });
  };

  /**
   * Accessor for mProductList. Since the lazy-typeahead data-provider within WriteReceipts.html is expecting a fn I needed to create this.
   */
  $ctrl.productList = function () {
    return mProductList;
  };

  /** Wrapper for scope event listener removal. */
  $ctrl.removeEventListener = function (type, handler) {
    $scope.$off(type, handler);
  };

  /*****************************************************************************
   * validateEntity
   */
  /*****************************************************************************
   * The user can select the entity by name or barcode and may add a new entity.
   * If not an entity object, check the value to see if it is a studentID and
   * select the entity matching that studentID. If it is a Receipt barcode, we
   * need to try to open the entityInfo dialog to display any open orders
   * associated with that receipt.
   *
   * @param entityOrInput
   *          Validate the entry from the typeahead. If <b>entityOrInput</b> is
   *          an object, this is an actual entity. Otherwise, this is a string
   *          that we need to run through the BarCode validator.
   * @param input
   *          Input element that triggered the blur
   * @param ignoreReceipt
   *          Ignore receipt barcodes flag
   * @return Promise that will resolve with an object containing the following
   *         properties: isEntity: Indication data is AddressEntityListItem.
   *         Receipt barcode string otherwise data: AddressEntityListItem/String
   *         add: Indication that a new entity was added
   ****************************************************************************/
  $ctrl.validateEntity = function (entityOrInput, input, ignoreReceipt) {
    if (!entityOrInput) return $q.reject();

    /*------------------------------------------*/
    /* Default values for the data object. When */
    /* isEntity is true, it is not a receipt */
    /*------------------------------------------*/
    var data = { isEntity: true, data: null, add: false };

    Alert.clear();

    /*----------------------------------------------------------------*/
    /* Select the entity from the data provider if selected. If there */
    /* is no selection, we need to make sure the discount is removed. */
    /*----------------------------------------------------------------*/
    if (typeof entityOrInput == 'object') {
      data.data = entityOrInput;

      return $q.when(data);
    } else {
      /*-----------------------------------------------------------------*/
      /* If not selected, find the student by the barcode input, if any. */
      /*-----------------------------------------------------------------*/
      var bc = BarcodeValidator.parse(entityOrInput);

      if (bc.isValid) {
        /*----------------------------------------------------------*/
        /* If a receipt barcode, resolve with parsed receipt number */
        /*----------------------------------------------------------*/
        if (bc.isReceipt && !ignoreReceipt) {
          data.isEntity = false;
          data.data = bc.number;
          return $q.when(data);
        } else {
          var defer = $q.defer();

          BooksSvc.getEntityByStudentID(bc.number, true).then(function (resp) {
            var sr = resp.data;

            if (sr) {
              data.data = sr;
              defer.resolve(data);
            } else {
              /*----------------------------------*/
              /* If not found, let the user know. */
              /*----------------------------------*/
              Alert.error(
                'No customer was found for the ID provided',
                'Invalid Barcode'
              );
              input.focus();
              defer.reject();
            }
          });

          return defer.promise;
        }
      } else if (!mRestrictAllEntities) {
        /*---------------------------------------------------------------------------*/
        /* If the input is not a valid name or barcode, assume a new name and allow  */
        /* the user to add a new entity. Otherwise, the model is text corresponding  */
        /* to the unmatched received from. Use the Entity Maker to process the input */
        /*---------------------------------------------------------------------------*/
        mEntityMakerActive = true;
        return EntityMaker.newEntity(entityOrInput)
          .then(
            function (result) {
              return $ctrl
                .getAddressEntityListItem(result.entity.id)
                .then(function (aeli) {
                  data.add = true;
                  data.data = aeli;
                  return data;
                });
            },
            function () {
              input.focus();
              return $q.reject();
            }
          )
          .finally(function () {
            mEntityMakerActive = false;
          });
      } else {
        /*----------------------------------------*/
        /* You should only get here if the user   */
        /* is not allowed to create any entities. */
        /*----------------------------------------*/
        input.value = '';
        return Alert.info(
          'You must select an existing entity',
          'Write New Receipt'
        );
      }
    }
  };

  /*****************************************************************************
   * Scope Code
   ****************************************************************************/
  $ctrl.$onInit = function () {
    angular.extend(mAppConfig, $ctrl.config);
    $ctrl.accountLabel = mAppConfig.acctPanelLabel;
    $ctrl.showNotes = mAppConfig.includeNotes;
  };
  /** General data. */ $ctrl.data = {};
  /** Payments match sales totals. */ $ctrl.mAmtMatch = false;
  /** Values for callapsible sections. */ $ctrl.notesCollapsed = true;
  /** Today's Date. */ $ctrl.today = new Date();
  /** csmValidator properties */ $ctrl.valGroup = {};

  /*----------------------------*/
  /* Payment Button Definitions */
  /*----------------------------*/
  $ctrl.paymentButtons = [
    {
      id: PMT_BUTTON_ID_CASH,
      label: 'Cash',
      hide: function () {
        return false;
      }
    },
    {
      id: PMT_BUTTON_ID_CHECK,
      label: 'Check',
      hide: function () {
        return false;
      }
    },
    {
      id: PMT_BUTTON_ID_CARD,
      label: 'Card',
      hide: function () {
        return !mUI_Flags.allowCreditCards;
      }
    },
    {
      id: PMT_BUTTON_ID_FUNDS,
      label: 'Funds',
      hide: function () {
        return !$ctrl.studentFunds();
      }
    }
  ];

  /*-------------------------*/
  /* Flag Accessor functions */
  /*-------------------------*/
  $ctrl.allowEditNumber = function () {
    return mUI_Flags.enableNumber;
  };
  $ctrl.allowEditReceipt = function () {
    return mUI_Flags.enableDateFrom;
  };
  $ctrl.allowItemUpdates = function () {
    return mUI_Flags.enableItems;
  };
  $ctrl.allowItemEffectiveDateEdit = function () {
    return mCanEditItemEffectiveDate;
  };
  $ctrl.allowPaymentUpdates = function () {
    return $ctrl.allowItemUpdates() && !mUI_Flags.isDeposited;
  };
  $ctrl.disableHandwritten = function () {
    return !mUI_Flags.enableHandwritten;
  };
  $ctrl.isVoided = function () {
    return mUI_Flags.isVoided;
  };
  $ctrl.isPickupReceipt = function () {
    return mUI_Flags.isPickupReceipt;
  };

  /** Void button shown for the following conditions. */
  $ctrl.showVoid = function () {
    return !mUI_Flags.isNewReceipt && !mUI_Flags.unvoidable && !mDisableVoid;
  };

  /** Student Funds Amount. */
  $ctrl.studentFunds = function () {
    return ($ctrl.data.from || { funds: 0 }).funds + editFundAmount();
  };

  /*****************************************************************************
   * displayStudentFunds
   */
  /*****************************************************************************
   * Returns the current student fund amount formatted as string if there is an
   * editFundAmount then it will be shown as a decremented value so the true
   * amount can be displayed.
   *
   * @return String to display for fund balance notification
   ****************************************************************************/
  $ctrl.displayStudentFunds = function () {
    return (
      $filter('currency')($ctrl.studentFunds()) +
      (editFundAmount() > 0 ? ' (-{0})'.format(editFundAmount()) : '')
    );
  };

  /*****************************************************************************
   * editItem
   */
  /*****************************************************************************
   * Called from the double click of a row or the click of the edit button in
   * the row. This is a request to perform an edit of the row by triggering the
   * appropriate edit input event so the item can be editted in the input
   * section if applicable.
   *
   * @param dgItem
   *          DGReceiptItem that is requesting to be editted
   ****************************************************************************/
  $ctrl.editItem = function (dgItem) {
    /*-----------------------*/
    /* No whole item editing */
    /*-----------------------*/
    if (!$ctrl.allowItemUpdates() || !dgItem.inputEditOnly()) return;

    /*-----------------------------------------------------*/
    /* Broadcast an event to start the edit process with */
    /* the current dgItem for the appropriate input */
    /* section. Ensuring that the section is not collapsed */
    /*-----------------------------------------------------*/
    if (dgItem.isOther()) {
      $scope.$broadcast(ReceiptEventTypes.EVENT_EDIT_ACCOUNT, dgItem);
      $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_PRODUCT);
      $ctrl.productsCollapsed = true;
      $ctrl.accountsCollapsed = false;
    } else {
      $scope.$broadcast(ReceiptEventTypes.EVENT_EDIT_PRODUCT, dgItem);
      $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_ACCOUNT);
      $ctrl.productsCollapsed = false;
      $ctrl.accountsCollapsed = true;
    }
  };

  /*****************************************************************************
   * entityLabel
   */
  /*****************************************************************************
   * Utilize the addressEntityLabel labelFunction when an object is provided.
   * This object is assumed to be an AddressEntityListItem. Otherwise this could
   * be just text from a non-entity association so we want the text to display.
   *
   * @param input
   *          Typeahead model value
   * @return string Label text to use
   ****************************************************************************/
  $ctrl.entityLabel = function (input) {
    if (angular.isObject(input)) return BooksSettings.addressEntityLabel(input);

    return input;
  };

  /*****************************************************************************
   * getVoidLabel
   */
  /*****************************************************************************
   * A receipt can be dropped instead of voided if there are no journal items
   * associated to it, otherwise, voiding is the only option.
   *
   * @return Text to display for the void button
   ****************************************************************************/
  $ctrl.getVoidLabel = function () {
    return $ctrl.receipt && mUI_Flags.isVoided ? 'Unvoid' : 'Void';

    /*-------------------------------------------------------------*/
    /* Removing the drop receipt functionality, see BOOKS-412 for  */
    /* details. Commenting out the functionality as it is possible */
    /* that we will be asked to put this back in at some point.    */
    /*-------------------------------------------------------------*/
    //return $ctrl.receipt && !$ctrl.receipt.hasJournalItems ? 'Drop' : mUI_Flags.isVoided ? 'Unvoid' : 'Void';
  };

  /*****************************************************************************
   * viewReturnReceipt
   */
  /*****************************************************************************
   * Opens one of $ctrl.receipt.returnReceipts - a return receipt that
   * reversed something on the receipt currently open (GR-2375, all five
   * return types). Follows the same clearReceipt()/getReceipt() pattern as
   * the Find/Next/Previous buttons, so unsaved changes on the CURRENT
   * receipt are confirmed before navigating away from it.
   *
   * @param event  Click event - only used for clearReceipt()'s
   *               focus-on-decline fallback.
   * @param rr     One entry from $ctrl.receipt.returnReceipts.
   ****************************************************************************/
  $ctrl.viewReturnReceipt = function (event, rr) {
    clearReceipt(event.target, function () {
      getReceipt(rr.id);
    });
  };

  /*****************************************************************************
   * handleButtons
   */
  /*****************************************************************************
   * Generic button handler Ensures the entity maker is not currently active
   * before continuing.
   *
   * @param event
   ****************************************************************************/
  $ctrl.handleButton = function (event) {
    if (mEntityMakerActive) return;

    switch (event.target.id) {
      case 'btnCancel':
        clearReceipt(event.target, startNewReceipt);
        break;
      case 'btnPrevious':
        clearReceipt(event.target, function () {
          findReceipt(-1);
        });
        break;
      case 'btnNext':
        clearReceipt(event.target, function () {
          findReceipt(1);
        });
        break;
      case 'btnFind':
        findReceipt();
        break;
      case 'btnInfo':
        entityInfo(null, true);
        break;
      case PMT_BUTTON_ID_CASH:
        addCash();
        break;
      case PMT_BUTTON_ID_CHECK:
        addCheck();
        break;
      case PMT_BUTTON_ID_CARD:
        addCard();
        break;
      case PMT_BUTTON_ID_FUNDS:
        addFunds();
        break;
      case 'btnSave':
        savePrecheck();
        break;
      case 'btnVoid':
        handleVoid();
        break;
      case 'btnPrint':
        printReceipt();
        break;
    }
  };

  /*****************************************************************************
   * handwrittenLabel
   */
  /*****************************************************************************
   * Label for the handwritten checkbox dependent on the defaultHandwritten
   * flag.
   ****************************************************************************/
  $ctrl.handwrittenLabel = function () {
    return mAppConfig.defaultHandwritten ? 'Printed?' : 'Handwritten?';
  };

  /*****************************************************************************
   * loadEntityList
   */
  /*****************************************************************************
   * Returns a list of entities that match the given text.
   *
   * @param value
   *          Current input text
   * @return Promise that will resolve with an array of AddressEntityListItems
   ****************************************************************************/
  $ctrl.loadEntityList = $ctrl.loadEntityList = function (value) {
    /*----------------------------------------------------*/
    /* When there is no input text, resolve with nothing, */
    /* this is called when the from model is cleared and */
    /* the lazyTypeahead is attempting to set the model */
    /*----------------------------------------------------*/
    if (!value) return $q.resolve();

    return BooksSvc.findAddressEntities(
      value,
      BooksSettings.ledgerEntityTypes(),
      true
    ).then(function (resp) {
      /*---------------------------------------------------------*/
      /* Return sorted entityList as data to resolve the promise */
      /*---------------------------------------------------------*/
      return resp.data.sort(function (a, b) {
        return a.entity.fullName
          .toLowerCase()
          .localeCompare(b.entity.fullName.toLowerCase());
      });
    });
  };

  /*****************************************************************************
   * onFrom
   */
  /*****************************************************************************
   * Fires when the 'Received From' field receives/loses focus and when the
   * enter key is pressed for potential barcode scanning. If the model is not
   * empty it will be validated, otherwise the current entity will be cleared.
   ****************************************************************************/
  $ctrl.onFrom = function ($event) {
    /*--------------------------------------*/
    /* On saved receipts, ignore From field */
    /* if no changes have been made to it. */
    /*--------------------------------------*/
    if (!mUI_Flags.isNewReceipt)
      if (
        typeof $ctrl.data.from == 'string' &&
        $ctrl.data.from.trim() == ($ctrl.receipt.receiptFrom || '').trim()
      )
        return;

    /*--------------------------------------------*/
    /* Save the current entity reference on focus */
    /*--------------------------------------------*/
    if ($event.type == 'focus') {
      mSavedEntity = $ctrl.data.from;
      return;
    }

    /*----------------------------------------------*/
    /* No from bound data, clear receipt properties */
    /*----------------------------------------------*/
    if (!$ctrl.data.from) selectEntity(null);
    /*--------------------------------------------------------------*/
    /* If the model is an object, or is a matching student ID, */
    /* there has been an entity selection and needs to be processed */
    /*--------------------------------------------------------------*/ else
      validateSelection($ctrl.data.from, $event.currentTarget);
  };

  /*****************************************************************************
   * openAccounts
   */
  /*****************************************************************************
   * Switches the open collapsible to the accounts if they can be edited and
   * clears the data in the Products tab.
   ****************************************************************************/
  $ctrl.openAccounts = function () {
    if ($ctrl.allowItemUpdates()) {
      $ctrl.accountsCollapsed = false;
      $ctrl.productsCollapsed = true;
      $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_PRODUCT);
      $scope.$broadcast(ReceiptEventTypes.EVENT_FOCUS_ACCOUNT);
    }
  };

  /*****************************************************************************
   * openProducts
   */
  /*****************************************************************************
   * Switches the open collapsible to the products if they can be edited and
   * clears the data in the Accouts tab.
   ****************************************************************************/
  $ctrl.openProducts = function () {
    if ($ctrl.allowItemUpdates()) {
      $ctrl.productsCollapsed = false;
      $ctrl.accountsCollapsed = true;
      $scope.$broadcast(ReceiptEventTypes.EVENT_RESET_ACCOUNT);
      $scope.$broadcast(ReceiptEventTypes.EVENT_FOCUS_PRODUCT);
    }
  };

  /*****************************************************************************
   * paymentTotal
   */
  /*****************************************************************************
   * Totals all the payments in the payGrid.
   ****************************************************************************/
  $ctrl.paymentTotal = function () {
    var total = 0;

    $ctrl.payGrid.data.forEach(function (payment) {
      total = Tools.roundToPrecision(total + payment.amount, 2);
    });

    return total;
  };

  /*****************************************************************************
   * readyNumber
   */
  /*****************************************************************************
   * Called when the receipt number field gets focus. Calling a timeout here
   * triggers a digest and allow the receipt model accessor logic to removes the
   * 'h' from the receipt number for an edit.
   ****************************************************************************/
  $ctrl.readyNumber = function () {
    $timeout();
  };

  /*****************************************************************************
   * salesTotal
   */
  /*****************************************************************************
   * Totals all the items in the rcptGrid.
   ****************************************************************************/
  $ctrl.salesTotal = function () {
    var total = 0;

    $ctrl.rcptGrid.data.forEach(function (item) {
      if (!item.isChildItem())
        total = Tools.roundToPrecision(total + Number(item.amount), 2);
    });

    return total;
  };

  /*------------------------------------------*/
  /* template to add ng-dblClick to each row. */
  /*------------------------------------------*/
  var dblClickTemplate =
    '<div ng-class="{\'limited\': row.entity.source().limiters && row.entity.source().limiters.length}" ng-dblclick="grid.appScope.$ctrl.editItem(row.entity)" ' +
    'ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" class="ui-grid-cell" ui-grid-cell></div>';

  /*------------------------------------------------------*/
  /* Edit Column cell template, contains a clickable span */
  /*------------------------------------------------------*/
  var editCellTemplate =
    '<div class="ui-grid-cell-contents">' +
    '<span class="csm-grid-space-target glyphicon glyphicon-edit" style="cursor:pointer" ng-click="grid.appScope.$ctrl.editItem(row.entity)"' +
    ' ng-show="row.entity.inputEditOnly()"></span></div>';

  /*------------------------------------------------------------------*/
  /* Used to limit the amount of characters a user can input when     */
  /* selecting a card payment on receipt. This is for the paygrid.    */
  /*                                                                  */
  /* Using makeEditorCellTemplate a string format is being performed. */
  /* To protect the braces we are using we escape both ends with an   */
  /* extra brace which is why there are three of them instead of two. */
  /* Negative one is used to represent no character limit             */
  /*------------------------------------------------------------------*/
  var numTemplate = CommonUIGridTemplates.makeEditorCellTemplate(
    'maxlength="{{{row.entity.isCard() ? 4 : -1}}}"',
    'INPUT_TYPE'
  );

  /**
   * Generic editCell handler which checks for a value change and sets the
   * modified flag.
   *
   * This also verifies that the amount to pay for an unpaid item is not more
   * than the balance of the receivable that is invalid in our system. Secondly we also check
   * that the amount to pay is not $0.00 for an unpaid item.
   *
   * @param dgItem {DGPaymentItem or DGReceiptItem} The item that is being edited.
   */
  function handleEditCell(dgItem, colDef, newValue, oldValue) {
    /**
     * Since the dgItem can be a payment or a receipt item we need to check if it is a receipt item
     * before we apply any further checks. Otherwise it will error every time it gets to the isPayment
     * check since that does not exist on DGPaymentItem.
     */
    if (dgItem instanceof Data.DGReceiptItem && dgItem.isPayment()) {
      const balance = dgItem.source().balance;

      if (dgItem.amount > balance) {
        Alert.info(
          `
        You can not pay more than the balance ($${balance.toFixed(2)}) of the Unpaid Item. 
        Resetting the Amount to Pay to $${balance.toFixed(2)}
      `,
          'Unpaid Items'
        );

        dgItem.amount = balance;
        return;
      } else if (dgItem.amount === 0) {
        Alert.info(
          `The Amount to Pay for the Unpaid Item can not be $0. Resetting the Amount to Pay to $${balance.toFixed(2)}`,
          'Unpaid Items'
        );

        dgItem.amount = balance;
        return;
      }
    }

    if (newValue != oldValue) {
      setModified(true);
    }
  }

  /** Receipt Item Grid Options */
  $ctrl.rcptGrid = {
    enableColumnMenus: false,
    cellEditableCondition: function () {
      return $ctrl.allowItemUpdates();
    },
    enableSorting: true,
    enableCellEdit: false,
    enableCellEditOnFocus: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 5,
    maxRowsToShow: 5,
    onRegisterApi: function (gridApi) {
      $ctrl.rcptGrid.api = gridApi;

      /*---------------------------*/
      /* Add an edit cell listener */
      /*---------------------------*/
      gridApi.edit.on.afterCellEdit($scope, handleEditCell);
    },
    rowTemplate: dblClickTemplate,
    columnDefs: [
      /*------*/
      /* Memo */
      /*------*/
      {
        field: 'memo',
        enableSorting: false,
        enableCellEdit: true,
        cellTemplate: prodCellTemplate,
        labelFunction: function (dgi) {
          return dgi.description();
        },
        cellEditableCondition: function (scope) {
          return scope.row.entity.allowEditMemo() && $ctrl.allowItemUpdates();
        }
      },
      /*-----*/
      /* Qty */
      /*-----*/
      {
        field: 'quantity',
        displayName: 'Qty',
        maxWidth: 60,
        enableSorting: false,
        cellFilter: 'number',
        type: 'number'
      },
      /*-------*/
      /* Price */
      /*-------*/
      {
        field: 'price',
        maxWidth: 100,
        enableSorting: false,
        cellFilter: 'currency',
        type: 'number'
      },
      /*-----*/
      /* Tax */
      /*-----*/
      {
        field: 'tax',
        maxWidth: 80,
        enableSorting: false,
        cellFilter: 'currency',
        labelFunction: productLabelFunction,
        type: 'number'
      },
      /*-------*/
      /* Total */
      /*-------*/
      {
        field: 'amount',
        displayName: 'Total',
        maxWidth: 100,
        enableSorting: false,
        enableCellEdit: true,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        labelFunction: function (dgi) {
          return dgi.isPickup() ? 'PICKUP' : $filter('currency')(dgi.amount);
        },
        cellEditableCondition: function (scope) {
          return scope.row.entity.allowEditAmount() && $ctrl.allowItemUpdates();
        }
      },
      /*------*/
      /* Edit */
      /*------*/
      {
        field: 'edit',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: editCellTemplate
      },
      /*--------*/
      /* Delete */
      /*--------*/
      {
        field: 'delete',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW
      }
    ]
  };

  /** Payments grid object */
  $ctrl.payGrid = {
    enableColumnMenus: false,
    cellEditableCondition: function () {
      return $ctrl.allowPaymentUpdates();
    },
    enableCellEditOnFocus: true,
    enableSorting: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 5,
    maxRowsToShow: 5,
    onRegisterApi: function (gridApi) {
      /*----------------------------------------------------------------*/
      /* Add the gridApi reference and a listener for the endEdit event */
      /*----------------------------------------------------------------*/
      ($ctrl.payGrid.api = gridApi).edit.on.afterCellEdit(
        $scope,
        handleEditCell
      );
    },
    columnDefs: [
      /*------*/
      /* Type */
      /*------*/
      {
        field: 'typeString',
        name: 'Type',
        maxWidth: 150,
        enableSorting: false,
        enableCellEdit: false
      },
      /*------*/
      /* Name */
      /*------*/
      {
        field: 'name',
        enableSorting: false,
        editableCellTemplate: CommonUIGridTemplates.CELL_DEFAULT,
        cellEditableCondition: function (scope) {
          return (
            scope.row.entity.allowDetailEdit() && $ctrl.allowPaymentUpdates()
          );
        }
      },
      /*--------*/
      /* Number */
      /*--------*/
      {
        field: 'number',
        maxWidth: 300,
        enableSorting: false,
        editableCellTemplate: numTemplate,
        cellEditableCondition: function (scope) {
          return (
            scope.row.entity.allowDetailEdit() && $ctrl.allowPaymentUpdates()
          );
        }
      },
      /*--------*/
      /* Amount */
      /*--------*/
      {
        field: 'amount',
        maxWidth: 90,
        enableSorting: false,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        allowNegative: false,
        cellFilter: 'currency'
      },
      /*--------*/
      /* Delete */
      /*--------*/
      {
        field: 'delete',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        enableCellEdit: false,
        cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW
      }
    ]
  };

  /*----------------------------------------*/
  /* Set up listener for the row delete */
  /* event and process for the provided row */
  /*----------------------------------------*/
  $scope.$on(
    CommonUIGridTemplates.EVENT_DELETE_ROW,
    function (event, grid, row) {
      /*-----------------------------*/
      /* Remove payment item request */
      /*-----------------------------*/
      if (grid.options == $ctrl.payGrid) deletePayment(row.entity);
      /*-----------------------------*/
      /* Remove receipt item request */
      /*-----------------------------*/ else if (grid.options == $ctrl.rcptGrid)
        deleteItem(row.entity);
    }
  );

  /*------------------*/
  /* Get server flags */
  /*------------------*/
  mDisableVoid = !!SERVER.appFlags.preventReceiptVoid;
  mRestrictReptHand = !!SERVER.appFlags.restrictReptsHand;

  /*--------------------------------------------------------------*/
  /* The entity restriction is now based on both the restrict     */
  /* vendors and restrict individuals. If only 1 is restricted we */
  /* want to allow the user to create the other type of entities. */
  /*--------------------------------------------------------------*/
  mRestrictAllEntities =
    !!SERVER.appFlags.restrictVendors && !!SERVER.appFlags.restrictIndividuals;
  /*------------------------------------------------------*/
  /* Restrict to use printed receipts only when the flag */
  /* is set. The current model if set needs to be updated */
  /*------------------------------------------------------*/
  if ($ctrl.receipt && mRestrictReptHand) $ctrl.receipt.handwritten = false;

  /*------------------------------------------------------------*/
  /* Watch for when the allowItemUpdates flag changes so when */
  /* updates are not allowed the update grid columns are hidden */
  /*------------------------------------------------------------*/
  $scope.$watch('allowItemUpdates()', function (allowed) {
    /*-----------------------------------------------------*/
    /* Get edit/delete column defs from receipt items grid */
    /*-----------------------------------------------------*/
    $ctrl.rcptGrid.columnDefs
      .filter(function (cd) {
        return ['edit', 'delete'].indexOf(cd.field) > -1;
      })

      /*------------------------------*/
      /* Update visibility of columns */
      /*------------------------------*/
      .forEach(function (cd) {
        cd.visible = allowed;
      });

    /*------------------------------*/
    /* Update for visibility change */
    /*------------------------------*/
    $ctrl.rcptGrid.api.grid.refresh();
  });

  /*----------------------------------------------------------------*/
  /* Update delete column visibility based on payment item updating */
  /*----------------------------------------------------------------*/
  $scope.$watch('allowPaymentUpdates()', function (allowed) {
    /*-----------------------------------------------------*/
    /* Get edit/delete column defs from receipt items grid */
    /*-----------------------------------------------------*/
    $ctrl.payGrid.columnDefs
      .filter(function (cd) {
        return cd.field == 'delete';
      })

      /*------------------------------*/
      /* Update visibility of columns */
      /*------------------------------*/
      .forEach(function (cd) {
        cd.visible = allowed;
      });

    /*------------------------------*/
    /* Update for visibility change */
    /*------------------------------*/
    $ctrl.payGrid.api.grid.refresh();
  });

  /*---------------------*/
  /* Initialize the app. */
  /*---------------------*/
  ReceiptsService.getReceiptScreenInfo().then(function (data) {
    mManualProcessor = data.manualProcessor;
    mUFAccountID = data.undepositedFundsAccountID;

    mProductList = data.products.sort(function (a, b) {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

    $ctrl.data.dbNextRcptNumHandwritten = data.handNum;
    $ctrl.data.dbNextRcptNumPrinted = data.printedNum;

    $ctrl.masterList = data.masterList;

    mCanEditItemEffectiveDate = !!data.canEditItemEffectiveDate;

    // Switched to false so we do not getNextReceiptNumbers since this request already gets that.
    startNewReceipt(false);
  });
}
